import axios from "axios";
import rsaController from "./RSA-controller";
import { hashSecretKey, hashPassword } from "./passwordHasher";
import { UserDocumentSubType } from "./enums";
import AppConstants from "./appConstants";
import { resetUiPreferencesToDefaults } from "./userPreferences";

const getAppSourceHeaderValue = () =>
  String(process.env.REACT_APP_APP_SOURCE ?? "0").trim() || "0";

axios.interceptors.request.use((config) => {
  const headers = config.headers || {};
  const appSource = getAppSourceHeaderValue();
  if (typeof headers.set === "function") {
    headers.set("app-source", appSource);
  } else {
    headers["app-source"] = appSource;
  }
  config.headers = headers;
  return config;
});

export const SHOW_SPLASH_EVENT = "mfg:show-splash";

/** Return to splash to retry bootstrap instead of showing a login error. */
export const returnToSplash = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(SHOW_SPLASH_EVENT));
};

const RESUME_LOGOUT_GRACE_MS = 4000;
let lastBecameVisibleAt = 0;
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) lastBecameVisibleAt = Date.now();
  });
}

/** Skip forced logout while the tab is hidden or just became visible again. */
export const shouldSkipForcedLogout = () => {
  if (typeof document !== "undefined" && document.hidden) return true;
  return Date.now() - lastBecameVisibleAt < RESUME_LOGOUT_GRACE_MS;
};

/**
 * REST API base. Override with REACT_APP_API_BASE_URL in .env.
 * Laravel RouteServiceProvider prefixes these routes with `/api`, so the base should end with `/api/`.
 * If you set only the site origin (e.g. https://example.com), `/api` is appended automatically.
 */
const normalizeApiBaseUrl = (raw) => {
  const fallback = "https://sandbox.mfginvest.com/api/";
  let s = (raw || "").trim();

  // In dev, route API via the CRA dev-server proxy to avoid CORS.
  if (
    process.env.NODE_ENV === "development" &&
    process.env.REACT_APP_API_PROXY_TARGET
  ) {
    return "/api/";
  }

  if (!s) return fallback;
  s = s.replace(/\/+$/, "");
  if (!/\/api$/i.test(s)) {
    s = `${s}/api`;
  }
  return `${s}/`;
};

const APIURI = normalizeApiBaseUrl(process.env.REACT_APP_API_BASE_URL);

const getSecretKey = () => {
  const envKey = process.env.REACT_APP_SECRET_KEY;

  if (!envKey) {
    throw new Error(
      "REACT_APP_SECRET_KEY is not set in environment variables. Please set it in your .env file and restart the development server.",
    );
  }

  return envKey;
};

const API_ROUTES = {
  LOGIN: `${APIURI}v1/user/login-client`,
  FCM_UPDATE: `${APIURI}v1/user/update-fcm-token`,
  FCM_DELETE: `${APIURI}v1/user/delete-fcm-token`,
  SERVER_PUBLIC_KEY: `${APIURI}get-server-public-key`,
  CREATE_USER_EXISTING_ACCOUNT: `${APIURI}v1/user/create-user-existing-account`,
  CREATE_USER_DEMO_ACCOUNT: `${APIURI}v1/user/create-user-demo-account`,
  VERIFY_EMAIL_DEMO_ACCOUNT: `${APIURI}v1/user/verify-email-demo-account`,
  VERIFY_OTP_DEMO_ACCOUNT: `${APIURI}v1/user/verify-otp-demo-account`,
  CREATE_USER_MT5_ACCOUNT: `${APIURI}v1/user/create-user-mt5-account`,
  CREATE_USER_LOGIN_MT5_ACCOUNT: `${APIURI}v1/user/create-user-login-mt5-account`,
  VERIFY_RESET_PIN_OTP: `${APIURI}v1/user/verify-reset-pin-otp`,
  SEND_RESET_PIN_OTP: `${APIURI}v1/user/send-reset-pin-otp`,
  SET_NEW_PASSWORD: `${APIURI}v1/user/set-new-password`,
  RESET_CRM_ACCOUNTS_PIN: `${APIURI}v1/user/reset-crm-accounts-pin`,
  RESET_MT5_PASSWORD: `${APIURI}v1/user/reset-mt5-password`,
  GET_FAQ: `${APIURI}v1/user/get-faq`,
  GET_CONTACT_US_INFO: `${APIURI}v1/user/get-contact-us-info`,
  GET_TUTORIAL_INFO: `${APIURI}v1/user/get-tutorial-info`,
  GET_APP_URL: `${APIURI}v1/user/get-app-url`,
  GET_HTML_PAGE: `${APIURI}v1/user/get-html-page`,
  CHECK_AGENT_ACCOUNT: `${APIURI}v1/user/check-agent-account`,
  CHECK_AGENT_TRADING_ACCOUNT: `${APIURI}v1/user/check-agent-trading-account`,
  VERIFY_EMAIL_DEVICE_ACCOUNT: `${APIURI}v1/user/verify-email-device-account`,
  VERIFY_OTP_DEVICE_ACCOUNT: `${APIURI}v1/user/verify-otp-device-account`,
  CREATE_URBAN_INFO_MT5_ACCOUNT: `${APIURI}v1/user/create-urban-info-mt5-account`,
  CREATE_REGULATION_COMPANY_MT5_ACCOUNT: `${APIURI}v1/user/create-regulation-company-mt5-account`,
  CREATE_USER_DOCUMENTS_MT5_ACCOUNT: `${APIURI}v1/user/create-user-documents-mt5-account`,
  GET_AGREEMENTS: `${APIURI}v1/user/get-agreements`,
  GET_PROFILE: `${APIURI}v1/user/get-profile`,
  UPDATE_PROFILE: `${APIURI}v1/user/update-profile`,
  UPDATE_PERSONAL_PROFILE: `${APIURI}v1/user/update-personal-profile`,
  UPDATE_PROFILE_STATUS: `${APIURI}v1/user/update-profile-status`,
  UPDATE_PERSONAL_INFORMATION: `${APIURI}v1/user/update-personal-information`,
  GET_HOME_PAGE_INFOS: `${APIURI}v1/user/get-home-page-infos`,
  GET_IB_DASHBOARD: `${APIURI}v1/user/get-ib-dashboard`,
  GET_BROKER_TOOLS: `${APIURI}v1/user/get-broker-tools`,
  SAVE_BROKER_EDITING_TEMPLATE: `${APIURI}v1/user/save-broker-editing-template`,
  GET_MARKETING_PLANS: `${APIURI}v1/user/get-marketing-plans`,
  UPDATE_EMAIL: `${APIURI}v1/user/update-email`,
  VALIDATE_OPT: `${APIURI}v1/user/validate-opt`,
  GET_TRADING_INFOS: `${APIURI}v1/user/get-trading-infos`,
  UPDATE_TRADING_INFOS: `${APIURI}v1/user/update-trading-infos`,
  SET_ACCOUNT_NAME: `${APIURI}v1/user/set-trading-name`,
  GET_FINANCIAL_INFOS: `${APIURI}v1/user/get-financial-infos`,
  UPDATE_FINANCIAL_INFOS: `${APIURI}v1/user/update-financial-infos`,
  DELETE_FINANCIAL_INFOS: `${APIURI}v1/user/delete-financial-infos`,
  GET_CASHIN_METHOD: `${APIURI}v1/user/get-cashin-method`,
  GET_TRADING_ACCOUNTS_TRX: `${APIURI}v1/user/get-trading-accounts-trx`,
  GET_TRADING_ACCOUNTS: `${APIURI}v1/user/get-trading-accounts`,
  // CTS (Customer Transaction Sheet)
  CHECK_CTS_MUST_SIGN: `${APIURI}v1/user/check-cts-must-sign`,
  VIEW_CTS_TODAY: `${APIURI}v1/user/view-cts-today`,
  CREATE_CTS_TODAY: `${APIURI}v1/user/create-cts-today`,
  SIGN_CTS: `${APIURI}v1/user/sign-cts`,
  // Cash-in
  CREATE_CASHIN_MONEY_OPERATION: `${APIURI}v1/user/create-cash-in-operation`,
  CANCEL_CASHIN_MONEY_OPERATION: `${APIURI}v1/user/cancel-cash-in-operation`,
  APPLY_CASHIN_MONEY_OPERATION: `${APIURI}v1/user/apply-cash-in-operation`,
  // Pay by card (wallet deposit via card gateway)
  CREATE_PAY_BY_CARD_OPERATION: `${APIURI}v1/user/create-pay-by-card-operation`,
  APPLY_PAY_BY_CARD_OPERATION: `${APIURI}v1/user/apply-pay-by-card-operation`,
  CANCEL_PAY_BY_CARD_OPERATION: `${APIURI}v1/user/cancel-pay-by-card-operation`,
  ADD_RESULT_PAY_BY_CARD_OPERATION: `${APIURI}v1/user/add-result-pay-by-card-operation`,
  CREATE_PAY_BY_CARD_HOSTED_PAYMENT: `${APIURI}v1/user/create-pay-by-card-hosted-payment`,
  // IB / Agent deposit / withdraw to client
  CREATE_DEPOSIT_WITHDRAW_OPERATION: `${APIURI}v1/user/create-deposit-withdraw-operation`,
  APPLY_DEPOSIT_WITHDRAW_OPERATION: `${APIURI}v1/user/apply-deposit-withdraw-operation`,
  CANCEL_DEPOSIT_WITHDRAW_OPERATION: `${APIURI}v1/user/cancel-deposit-withdraw-operation`,
  SEND_NOTIFICATIONS: `${APIURI}v1/user/send-notifications`,
  // Internal transfer
  CREATE_INTERNAL_TRANSFER_MONEY_OPERATION: `${APIURI}v1/user/create-internal-transfer-operation`,
  CANCEL_INTERNAL_TRANSFER_MONEY_OPERATION: `${APIURI}v1/user/cancel-internal-transfer-operation`,
  APPLY_INTERNAL_TRANSFER_MONEY_OPERATION: `${APIURI}v1/user/apply-internal-transfer-operation`,
  // Cash-out (withdrawal)
  GET_PAYMENT_METHOD: `${APIURI}v1/user/get-payment-method`,
  CREATE_CASH_OUT_OPERATION: `${APIURI}v1/user/create-cash-out-operation`,
  CANCEL_CASH_OUT_OPERATION: `${APIURI}v1/user/cancel-cash-out-operation`,
  APPLY_CASH_OUT_OPERATION: `${APIURI}v1/user/apply-cash-out-operation`,
  // Pending cash-out requests
  GET_ALL_PENDING_CASHOUT: `${APIURI}v1/user/get-all-pending-cashout`,
  GET_CASHOUT: `${APIURI}v1/user/get-cashout`,
  CANCEL_CASH_OUT_REQUEST: `${APIURI}v1/user/cancel-cash-out-request`,
  // Exchange
  GET_EXCHANGE_CURRENCIES: `${APIURI}v1/user/get-exchange-currencies`,
  GET_EXCHANGE_RATES: `${APIURI}v1/user/get-exchange-rates`,
  CALCULATE_EXCHANGE_RATE: `${APIURI}v1/user/calculate-exchange-rate`,
  CANCEL_EXCHANGE_RATE: `${APIURI}v1/user/cancel-exchange-rate`,
  APPLY_EXCHANGE_RATE: `${APIURI}v1/user/apply-exchange-rate`,
  // Wallets / e-wallets
  GET_ALL_EWALLETS_INFO: `${APIURI}v1/user/get-all-ewallets-info`,
  GET_ALL_EWALLETS_AMOUNT: `${APIURI}v1/user/get-all-ewallets-amount`,
  // Trading accounts
  CREATE_NEW_TRADING_ACCOUNT: `${APIURI}v1/user/create-new-trading-account`,
  // IB / Agent
  GET_ALL_AGENT_CLIENTS: `${APIURI}v1/user/get-all-agent-clients`,
  GET_AGENT_CLIENT_ACCOUNTS: `${APIURI}v1/user/get-agent-client-accounts`,
  GET_TRADING_ACCOUNT_INFOS: `${APIURI}v1/user/get-trading-account-infos`,
  GET_CLIENTS_TRADING_HISTORY: `${APIURI}v1/user/get-clients-trading-history`,
  GET_CLIENTS_ACCOUNT_TOTALS: `${APIURI}v1/user/get-clients-account-totals`,
  GET_ACCOUNT_TRADING_HISTORY: `${APIURI}v1/user/get-account-trading-history`,
  GET_USER_TRADING_VOLUME: `${APIURI}v1/user/get-user-trading-volume`,
  // Chat
  GET_CHAT_MESSAGE: `${APIURI}v1/user/get-chat-message`,
  GET_A_CHAT_MESSAGE: `${APIURI}v1/user/get-a-message`,
  ADD_CHAT_MESSAGE: `${APIURI}v1/user/add-chat-message`,
  ADD_CHAT_DOCUMENT: `${APIURI}v1/user/add-chat-document`,
  GET_CHAT_DOCUMENT: `${APIURI}v1/user/get-chat-document`,
  // Demo trading - deposit operation
  CREATE_DEPOSIT_DEMO_OPERATION: `${APIURI}v1/user/create-deposit-demo-operation`,
  APPLY_DEPOSIT_DEMO_OPERATION: `${APIURI}v1/user/apply-deposit-demo-operation`,
  CANCEL_DEPOSIT_DEMO_OPERATION: `${APIURI}v1/user/cancel-deposit-demo-operation`,
  // Transactions
  GET_ALL_TRANSACTIONS: `${APIURI}v1/user/get-all-transactions`,
  GET_TRANSACTION: `${APIURI}v1/user/get-transaction`,
  GET_CASHIN_GATEWAY_INFORMATION: `${APIURI}v1/user/get-cashin-gateway-information`,
  // Cancel pending transaction (mirrors Flutter APIURI.CANCEL_TRANSACTIONS)
  CANCEL_PENDING_TRANSACTION: `${APIURI}v1/user/cancel-pending-transaction`,
  // Pending cash-in requests
  GET_ALL_PENDING_CASHIN: `${APIURI}v1/user/get-all-pending-cashin`,
  GET_PENDING_CASHIN_GATEWAY_INFORMATION: `${APIURI}v1/user/get-pending-cashin-gateway-information`,
  GET_CASHIN: `${APIURI}v1/user/get-cashin`,
  CANCEL_CASH_IN_REQUEST: `${APIURI}v1/user/cancel-cash-in-request`,
  // Broker collect / transfer (cash requests)
  GET_COLLECTED_MONEY_REQUEST: `${APIURI}v1/user/get-collected-money-request`,
  APPLY_COLLECT_CASH_IN_REQUEST: `${APIURI}v1/user/apply-collect-cash-in-request`,
  APPLY_COLLECT_CASH_OUT_REQUEST: `${APIURI}v1/user/apply-collect-cash-out-request`,
  GET_TRANSFER_CASH_OUT_REQUEST: `${APIURI}v1/user/get-transfer-cash-out-request`,
  APPLY_TRANSFER_CASH_OUT_REQUEST: `${APIURI}v1/user/apply-transfer-cash-out-request`,
  // Settings
  UPDATE_PIN_CODE: `${APIURI}v1/user/update-pin-code`,
  GET_USER_NOTIFICATION_SETTING: `${APIURI}v1/user/get-user-notification-setting`,
  GET_NOTIFICATIONS: `${APIURI}v1/user/get-notifications`,
  UPDATE_USER_NOTIFICATION_SETTING: `${APIURI}v1/user/update-user-notification-setting`,
  DELETE_USER_ACCOUNT: `${APIURI}v1/user/delete-user-account`,
  SIGN_FORCED_AGREEMENT: `${APIURI}v1/user/sign-forced-agreement`,
  GET_FILE_DOCUMENT: `${APIURI}v1/user/get-file-document`,
  // Device management
  GET_DEVICES_LIST: `${APIURI}v1/user/get-devices-list`,
  GET_DEVICE: `${APIURI}v1/user/get-device`,
  UPDATE_DEVICE: `${APIURI}v1/user/update-device`,
  DELETE_DEVICE: `${APIURI}v1/user/delete-device`,
  // Copy Trade
  GET_COPY_TRADE_MASTERS: `${APIURI}v1/user/copy-trade/masters/list`,
  GET_COPY_TRADE_MASTER_STATUS: `${APIURI}v1/user/copy-trade/masters/status`,
  BECOME_COPY_TRADE_MASTER: `${APIURI}v1/user/copy-trade/masters/become`,
  UPDATE_COPY_TRADE_MASTER: `${APIURI}v1/user/copy-trade/masters/update`,
  GET_COPY_TRADE_MASTER_SUBSCRIBERS: `${APIURI}v1/user/copy-trade/masters/subscribers`,
  GET_COPY_TRADE_MASTER_TRADES: `${APIURI}v1/user/copy-trade/masters/trades`,
  GET_COPY_TRADE_MASTER_PERFORMANCE: `${APIURI}v1/user/copy-trade/masters/performance`,
  COPY_TRADE_SUBSCRIBE: `${APIURI}v1/user/copy-trade/subscribe`,
  COPY_TRADE_MY_SUBSCRIPTIONS: `${APIURI}v1/user/copy-trade/my-subscriptions`,
  COPY_TRADE_UNSUBSCRIBE: `${APIURI}v1/user/copy-trade/unsubscribe`,
  GET_COPY_TRADE_SYMBOL_QUOTES: `${APIURI}v1/user/copy-trade/symbols/quotes`,
};

const urlNeedPublicKeys = [
  API_ROUTES.LOGIN,
  API_ROUTES.FCM_UPDATE,
  API_ROUTES.FCM_DELETE,
  API_ROUTES.SERVER_PUBLIC_KEY,
  API_ROUTES.CREATE_USER_EXISTING_ACCOUNT,
  API_ROUTES.CREATE_USER_DEMO_ACCOUNT,
  API_ROUTES.VERIFY_EMAIL_DEMO_ACCOUNT,
  API_ROUTES.VERIFY_OTP_DEMO_ACCOUNT,
  API_ROUTES.CREATE_USER_MT5_ACCOUNT,
  API_ROUTES.VERIFY_RESET_PIN_OTP,
  API_ROUTES.SEND_RESET_PIN_OTP,
  API_ROUTES.SET_NEW_PASSWORD,
  API_ROUTES.RESET_CRM_ACCOUNTS_PIN,
  API_ROUTES.GET_FAQ,
  API_ROUTES.GET_CONTACT_US_INFO ,
  API_ROUTES.GET_USER_NOTIFICATION_SETTING,
  API_ROUTES.UPDATE_USER_NOTIFICATION_SETTING,
  API_ROUTES.DELETE_USER_ACCOUNT,
  API_ROUTES.CHECK_AGENT_ACCOUNT,
  API_ROUTES.CHECK_AGENT_TRADING_ACCOUNT,
  API_ROUTES.VERIFY_EMAIL_DEVICE_ACCOUNT,
  API_ROUTES.VERIFY_OTP_DEVICE_ACCOUNT,
  API_ROUTES.CREATE_URBAN_INFO_MT5_ACCOUNT,
  API_ROUTES.CREATE_REGULATION_COMPANY_MT5_ACCOUNT, 
  API_ROUTES.GET_PROFILE,
  API_ROUTES.UPDATE_EMAIL,
  API_ROUTES.VALIDATE_OPT,
  API_ROUTES.UPDATE_FINANCIAL_INFOS,
  API_ROUTES.DELETE_FINANCIAL_INFOS,
  API_ROUTES.CREATE_NEW_TRADING_ACCOUNT,
  API_ROUTES.GET_TUTORIAL_INFO,
  API_ROUTES.GET_APP_URL,
  // Note: GET_HOME_PAGE_INFOS, GET_TRADING_INFOS, and GET_FINANCIAL_INFOS are NOT in urlNeedPublicKeys (matches Flutter implementation)
];

const getDeviceOS = () => {
  const userAgent = navigator.userAgent || navigator.platform || "Unknown";

  if (/Win/.test(userAgent)) return "Windows";
  if (/Mac/.test(userAgent)) return "MacOS";
  if (/Linux/.test(userAgent)) return "Linux";
  if (/Android/.test(userAgent)) return "Android";
  if (/iPhone|iPad|iPod/.test(userAgent)) return "iOS";

  return navigator.platform || "Unknown";
};

const getDeviceModel = () => {
  const userAgent = navigator.userAgent || "";

  if (/iPhone/.test(userAgent)) {
    const match = userAgent.match(/iPhone(\d+,\d+)/);
    return match ? `iPhone ${match[1]}` : "iPhone";
  }
  if (/iPad/.test(userAgent)) {
    const match = userAgent.match(/iPad(\d+,\d+)/);
    return match ? `iPad ${match[1]}` : "iPad";
  }
  if (/Android/.test(userAgent)) {
    const match = userAgent.match(/Android.*?;\s([^)]+)\)/);
    return match ? match[1] : "Android Device";
  }

  return navigator.platform || "Unknown";
};

const getDeviceName = () => {
  if (typeof window !== "undefined" && window.location) {
    return window.location.hostname || "Unknown Device";
  }
  return "Unknown Device";
};

const DEVICE_STORAGE_KEYS = {
  DEVICE_ID: "mfginvest_device_id",
  DEVICE_MODEL: "mfginvest_device_model",
  DEVICE_OS: "mfginvest_device_os",
};

const getStoredDeviceHeaders = () => {
  try {
    const id =
      sessionStorage.getItem(DEVICE_STORAGE_KEYS.DEVICE_ID) ||
      localStorage.getItem(DEVICE_STORAGE_KEYS.DEVICE_ID);
    const model =
      sessionStorage.getItem(DEVICE_STORAGE_KEYS.DEVICE_MODEL) ||
      localStorage.getItem(DEVICE_STORAGE_KEYS.DEVICE_MODEL);
    const os =
      sessionStorage.getItem(DEVICE_STORAGE_KEYS.DEVICE_OS) ||
      localStorage.getItem(DEVICE_STORAGE_KEYS.DEVICE_OS);
    if (id && model && os)
      return { "device-id": id, "device-model": model, "device-os": os };
  } catch (_) {}
  return null;
};

const setStoredDeviceHeaders = (deviceId, deviceModel, deviceOs) => {
  try {
    for (const s of [sessionStorage, localStorage]) {
      if (deviceId != null)
        s.setItem(DEVICE_STORAGE_KEYS.DEVICE_ID, String(deviceId));
      if (deviceModel != null)
        s.setItem(DEVICE_STORAGE_KEYS.DEVICE_MODEL, String(deviceModel));
      if (deviceOs != null)
        s.setItem(DEVICE_STORAGE_KEYS.DEVICE_OS, String(deviceOs));
    }
  } catch (_) {}
};

const clearStoredDeviceHeaders = () => {
  try {
    for (const s of [sessionStorage, localStorage]) {
      s.removeItem(DEVICE_STORAGE_KEYS.DEVICE_ID);
      s.removeItem(DEVICE_STORAGE_KEYS.DEVICE_MODEL);
      s.removeItem(DEVICE_STORAGE_KEYS.DEVICE_OS);
    }
  } catch (_) {}
};

/**
 * Logout utility — clears session and redirects to splash.
 * Used for critical auth/session failures (e.g. invalid SecretKey), not for
 * transient Axios "Network Error" (CORS, timeouts, unreachable host, etc.).
 */
const handleNetworkErrorLogout = () => {
  try {
    // Clear token from storage
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");

    // Clear user ID
    localStorage.removeItem("userId");
    sessionStorage.removeItem("userId");

    // IMPORTANT: Do NOT clear device headers here.
    // The web app generates a random device-id (unlike Flutter which uses
    // real hardware IDs). If we clear it, a new device-id is generated on
    // re-login and the backend treats it as a brand-new unverified device,
    // forcing unnecessary device verification (501 LOGIN_WAIT_ACCESS).
    // Keeping the same device-id matches Flutter behavior where the device
    // identity persists across sessions.

    // Clear RSA keys
    rsaController.clearUserKeys();

    // Clear login status
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("userEmail");

    // Clear any other stored data
    localStorage.removeItem("registration_step1_data");
    sessionStorage.removeItem("registration_step1_data");
    resetUiPreferencesToDefaults();

    // Prevent next login from reusing previous session's home / user caches (must match HOME_PAGE_INFOS_CACHE_KEY).
    try {
      sessionStorage.removeItem("homePageInfosCacheV1");
      sessionStorage.removeItem("userData");
      sessionStorage.removeItem("userDataTimestamp");
    } catch (_) {}

    // Redirect to splash page (home page will show splash screen)
    if (typeof window !== "undefined") {
      window.location.href = "/";
    }
  } catch (error) {
    // If logout fails, still try to redirect
    if (typeof window !== "undefined") {
      window.location.href = "/";
    }
  }
};

export const forceSessionLogout = () => {
  handleNetworkErrorLogout();
};

const getDevicePosition = (options = {}) => {
  const {
    enableHighAccuracy = false,
    timeout = 15000,
    maximumAge = 60000,
  } = options;
  return new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve({ latitude: null, longitude: null, error: "unsupported" });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          error: null,
        });
      },
      (error) => {
        resolve({
          latitude: null,
          longitude: null,
          error:
            error.code === 1
              ? "denied"
              : error.code === 2
                ? "unavailable"
                : "timeout",
        });
      },
      { enableHighAccuracy, timeout, maximumAge },
    );
  });
};

/**
 * Ensure we have location. Use before post-login actions (cash-in, CTS, etc.).
 * Uses cached position when available (up to 1 min). Single attempt, no retry.
 * @returns {Promise<{ ok: boolean, latitude?: number, longitude?: number, error?: string }>}
 */
const ensureLocation = async () => {
  const result = await getDevicePosition({
    maximumAge: 60000,
    timeout: 15000,
    enableHighAccuracy: false,
  });
  if (result.latitude != null && result.longitude != null) {
    return { ok: true, latitude: result.latitude, longitude: result.longitude };
  }
  return {
    ok: false,
    error:
      result.error === "denied"
        ? "location_denied"
        : result.error === "unavailable"
          ? "location_unavailable"
          : "location_required",
  };
};

// const getDeviceId = () => {
//   const storageKey = 'mfginvest_device_id';
//   let deviceId = localStorage.getItem(storageKey);

//   if (!deviceId) {
//     deviceId = 'WEB-' + Math.random().toString(36).substring(2, 10).toUpperCase() +
//                 Date.now().toString(36).substring(2, 8).toUpperCase();
//     localStorage.setItem(storageKey, deviceId);
//   }

//   return deviceId;
// };

const getOrCreateWebDeviceIdentity = () => {
  try {
    const stored = getStoredDeviceHeaders();
    if (
      stored &&
      stored["device-id"] &&
      stored["device-model"] &&
      stored["device-os"]
    ) {
      return stored;
    }

    const deviceOs = getDeviceOS();
    const deviceModel = getDeviceModel();

    const deviceId =
      "WEB-" +
      Math.random().toString(36).substring(2, 10).toUpperCase() +
      Date.now().toString(36).substring(2, 8).toUpperCase();

    setStoredDeviceHeaders(deviceId, deviceModel, deviceOs);

    return {
      "device-id": deviceId,
      "device-model": deviceModel,
      "device-os": deviceOs,
    };
  } catch (_) {
    const deviceOs = getDeviceOS();
    const deviceModel = getDeviceModel();
    return {
      "device-id": `WEB-${deviceModel || "Unknown"}-${deviceOs || "Unknown"}`,
      "device-model": deviceModel,
      "device-os": deviceOs,
    };
  }
};

const buildExtraInfo = async (options = {}) => {
  const { useLocation = true, useStoredDevice = true } = options;
  const keyToUse = getSecretKey();
  const hashedSecretKey = await hashSecretKey(keyToUse);

  let position = { latitude: 0, longitude: 0 };
  if (useLocation) {
    const loc = await getDevicePosition();
    if (loc.latitude != null && loc.longitude != null) {
      position = { latitude: loc.latitude, longitude: loc.longitude };
    }
  }

  let deviceId;
  let deviceOs;
  let deviceModel;

  if (useStoredDevice) {
    const identity = getOrCreateWebDeviceIdentity();
    deviceId = identity["device-id"];
    deviceOs = identity["device-os"];
    deviceModel = identity["device-model"];
  } else {
    deviceOs = getDeviceOS();
    deviceModel = getDeviceModel();
    deviceId = getDeviceName() + "-" + deviceOs;
  }

  const extraInfo = {
    "device-id": deviceId,
    "device-os": deviceOs,
    "device-model": deviceModel,
    "device-name": getDeviceName(),
    "device-ip": "null",
    "device-position": JSON.stringify({
      LATITUDE: position.latitude,
      LONGTITUDE: position.longitude,
    }),
    "secret-key": hashedSecretKey,
    "user-type": "0",
    "app-source": getAppSourceHeaderValue(),
  };

  return extraInfo;
};

const encryptData = async (data) => {
  try {
    return data;
    // const dataString = JSON.stringify(data);
    // const segmentSizeBits = 2048;
    // const segmentSizeBytes = segmentSizeBits / 8;

    // const encoder = new TextEncoder();
    // const dataBytes = encoder.encode(dataString);
    // const dataByteLength = dataBytes.length;

    // if (dataByteLength <= segmentSizeBytes) {
    //   return await rsaController.encryptWithServerKey(dataString);
    // } else {
    //   const numSegments = Math.ceil(dataByteLength / segmentSizeBytes);
    //   const segments = [];

    //   for (let i = 0; i < numSegments; i++) {
    //     const startByte = i * segmentSizeBytes;
    //     const endByte = Math.min((i + 1) * segmentSizeBytes, dataByteLength);
    //     const segmentBytes = dataBytes.slice(startByte, endByte);
    //     const segment = new TextDecoder().decode(segmentBytes);
    //     segments.push(segment);
    //   }

    //   const segmentsEncrypted = [];
    //   for (const segment of segments) {
    //     const encryptedChunk = await rsaController.encryptWithServerKey(segment);
    //     segmentsEncrypted.push(encryptedChunk);
    //   }

    //   return segmentsEncrypted;
    // }
  } catch (error) {
    throw new Error(`Failed to encrypt data: ${error.message}`);
  }
};

/**
 * Extract API payload from a response without RSA decryption.
 * Supports plain JSON in MESSAGES (objects or strings), and legacy response shapes.
 */
const extractApiResponseData = (responseData) => {
  if (responseData == null) return null;

  // MESSAGES as plain object: { CODE, PUBLIC_KEY, ... } (sandbox server public key shape)
  if (
    responseData.MESSAGES &&
    typeof responseData.MESSAGES === "object" &&
    !Array.isArray(responseData.MESSAGES)
  ) {
    return responseData.MESSAGES;
  }

  if (responseData.MESSAGES && Array.isArray(responseData.MESSAGES)) {
    const messages = responseData.MESSAGES.filter(
      (m) => m != null && m !== "",
    );
    if (messages.length === 0) return responseData;

    if (typeof messages[0] === "object") {
      return messages.length === 1 ? messages[0] : messages;
    }

    let jsonString = "";
    for (const seg of messages) {
      if (typeof seg === "object") return seg;
      if (typeof seg !== "string" || !seg.trim()) continue;
      const trimmed = seg.trim();
      if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
        try {
          return JSON.parse(trimmed);
        } catch {
          jsonString += seg;
        }
      } else {
        jsonString += seg;
      }
    }

    if (jsonString) {
      const cleaned = jsonString.trim();
      try {
        return JSON.parse(cleaned);
      } catch {
        const codeMatch = cleaned.match(/"CODE"\s*:\s*(\d+)/);
        if (codeMatch) return { CODE: parseInt(codeMatch[1], 10) };
        if (
          cleaned.includes("BEGIN PUBLIC KEY") ||
          cleaned.includes("BEGIN RSA PUBLIC KEY")
        ) {
          return cleaned;
        }
        return responseData;
      }
    }
    return responseData;
  }

  if (typeof responseData === "string") {
    const trimmed = responseData.trim();
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        return JSON.parse(trimmed);
      } catch {
        return responseData;
      }
    }
    return responseData;
  }

  return responseData;
};

/**
 * Parse response data from MESSAGES array (no RSA decryption).
 * @param {object} responseData - Response data object that may contain MESSAGES array
 * @returns {Promise<object|null>} Parsed response object or null if parsing fails
 */
const decryptResponseMessages = async (responseData) => {
  try {
    return extractApiResponseData(responseData);
  } catch (_) {
    return responseData || null;
  }
};

/**
 * Normalize API payload from response (plain JSON or MESSAGES object/array).
 */
const getApiResponsePayload = (response) => {
  if (response == null) return null;
  const source =
    response.decryptedData !== undefined && response.decryptedData !== null
      ? response.decryptedData
      : response.data !== undefined
        ? response.data
        : response;
  if (source == null) return null;
  const extracted = extractApiResponseData(source);
  return extracted ?? source;
};

/**
 * Read CODE from any supported response shape.
 */
const getResponseCode = (response) => {
  const payload = getApiResponsePayload(response);
  if (payload == null) return null;
  let code =
    payload.CODE ??
    payload.code ??
    payload.body?.CODE ??
    payload.body?.code ??
    payload.data?.CODE ??
    payload.data?.code;
  if (
    code == null &&
    payload.MESSAGES &&
    typeof payload.MESSAGES === "object" &&
    !Array.isArray(payload.MESSAGES)
  ) {
    code = payload.MESSAGES.CODE ?? payload.MESSAGES.code;
  }
  if (code != null && typeof code === "string") {
    const n = parseInt(code, 10);
    return Number.isNaN(n) ? code : n;
  }
  return code ?? null;
};

const isApiResponseOk = (response, extraCodes = []) => {
  const code = getResponseCode(response);
  if (code == null) return false;
  const okCodes = [
    AppConstants.responseOk,
    AppConstants.messagesOK,
    ...extraCodes,
  ];
  return okCodes.includes(code);
};

const needsPublicKey = (url) => {
  return urlNeedPublicKeys.includes(url);
};

const isServerPublicKeyEndpoint = (url) => {
  return url === API_ROUTES.SERVER_PUBLIC_KEY;
};

const getUserPublicKey = async () => {
  try {
    return await rsaController.getUserPublicKey();
  } catch (error) {
    throw new Error("Failed to get user public key");
  }
};


const resolveResponseDecryptedData = async (response) => {
  try {
    const parsed = await decryptResponseMessages(response?.data);
    if (parsed != null) return parsed;

    const data = response?.data;
    if (!data) return null;

    if (data.body && typeof data.body === "string" && data.body.length > 50) {
      return await decryptResponse(data.body);
    }
    if (typeof data === "string" && data.length > 50) {
      return await decryptResponse(data);
    }
    if (data.data && typeof data.data === "string" && data.data.length > 50) {
      return await decryptResponse(data.data);
    }
    if (
      data.encrypted &&
      typeof data.encrypted === "string" &&
      data.encrypted.length > 50
    ) {
      return await decryptResponse(data.encrypted);
    }
    if (
      data.message &&
      typeof data.message === "string" &&
      data.message.length > 50
    ) {
      return await decryptResponse(data.message);
    }
    if (
      data.result &&
      typeof data.result === "string" &&
      data.result.length > 50
    ) {
      return await decryptResponse(data.result);
    }
    return data;
  } catch (_) {
    return response?.data ?? null;
  }
};

const decryptResponse = async (encryptedData) => {
  try {
    return extractApiResponseData(encryptedData);
  } catch (_) {
    return encryptedData ?? null;
  }
};

const postDataToServer = async (url, body, options = {}) => {
  const {
    mainHeaders = {},
    baseURL = "",
    // Milliseconds; omit for axios default (no timeout). Used for bootstrap calls.
    timeout,
  } = options;

  try {
    let processedBody = {};

    if (body.email) {
      processedBody.USERNAME = body.email;
    }
    if (body.password && typeof body.password === "string") {
      processedBody.PASSWORD = await hashPassword(body.password);
    }

    for (const [key, value] of Object.entries(body)) {
      if (key !== "email" && key !== "password") {
        processedBody[key] = value;
      }
    }

    // Always get secret key from environment variable and hash it
    // Do NOT trigger geolocation for non-interactive bootstrap calls
    // (e.g. login and initial server-public-key fetch), to avoid
    // "Only request geolocation information in response to a user gesture"
    // violations and unnecessary permission prompts.
    const shouldSkipLocation =
      url === API_ROUTES.LOGIN || isServerPublicKeyEndpoint(url);

    const extraInfo = await buildExtraInfo(
      shouldSkipLocation ? { useLocation: false } : {},
    );

    let headers = {
      "Content-Type": "application/json; charset=UTF-8",
      ...mainHeaders,
    };

    const headerInfo = {};
    for (const [key, value] of Object.entries(extraInfo)) {
      if (value !== null && value !== undefined) {
        headerInfo[key] = String(value);
      }
    }

    if (isServerPublicKeyEndpoint(url)) {
      headers = {
        ...headers,
        ...headerInfo,
      };
    }
    if (url === API_ROUTES.LOGIN) {
      const did = extraInfo["device-id"] ?? "";
      const dmodel = extraInfo["device-model"] ?? "";
      const dos = extraInfo["device-os"] ?? "";
      headers["device-id"] = did;
      headers["device-model"] = dmodel;
      headers["device-os"] = dos;
      headers["user-type"] = String(extraInfo["user-type"] ?? "0");
      setStoredDeviceHeaders(did, dmodel, dos);
    }

    let requestBody;

    if (needsPublicKey(url)) {
      if (isServerPublicKeyEndpoint(url)) {
        const publicKey = await getUserPublicKey();
        requestBody = {
          PUBLIC_KEY: publicKey,
          TYPE: "0",
          VERSION_CODE: "0.5.9",
        };
      } else {
        const dataToEncrypt = {
          HEADERS: extraInfo,
          BODY: processedBody,
        };

        try {
          const encryptedResult = await encryptData(dataToEncrypt);
          const publicKey = await getUserPublicKey();

          const messagesArray = Array.isArray(encryptedResult)
            ? encryptedResult
            : [encryptedResult];
          requestBody = {
            PUBLIC_KEY: publicKey,
            MESSAGES: messagesArray,
            TYPE: "0",
            VERSION_CODE: "0.5.9",
          };
        } catch (encryptError) {
          throw new Error(`Failed to encrypt data: ${encryptError.message}`);
        }
      }
    } else {
      const encryptedBody = await encryptData(processedBody);
      requestBody = {
        header: extraInfo,
        body: encryptedBody,
      };
    }

    let fullUrl;
    if (url.startsWith("http://") || url.startsWith("https://")) {
      fullUrl = url;
    } else if (baseURL) {
      fullUrl = `${baseURL}/${url}`;
    } else {
      fullUrl = url;
    }

    // Console logging for debugging
    if (
      url === API_ROUTES.SEND_RESET_PIN_OTP ||
      url === API_ROUTES.VERIFY_RESET_PIN_OTP
    ) {
      if (requestBody.MESSAGES && Array.isArray(requestBody.MESSAGES)) {
        // MESSAGES array exists
      }
    }

    let response;
    try {
      const axiosConfig = { headers };
      if (typeof timeout === "number" && timeout > 0) {
        axiosConfig.timeout = timeout;
      }
      response = await axios.post(fullUrl, requestBody, axiosConfig);

      // Log server response for reset PIN OTP endpoints
      if (
        url === API_ROUTES.SEND_RESET_PIN_OTP ||
        url === API_ROUTES.VERIFY_RESET_PIN_OTP
      ) {
        // Response logged
      }
    } catch (error) {
      // Axios "Network Error" means no HTTP response (CORS, DNS, offline, etc.) —
      // do not force logout; the user's session may still be valid.
      const isNetworkError =
        !error.response &&
        (error.code === "ERR_NETWORK" ||
          error.message === "Network Error" ||
          error.message?.includes("Network Error") ||
          error.message?.includes("ERR_NETWORK"));

      if (isNetworkError) {
        const networkError = new Error(
          "Network error — could not reach the server. Please try again.",
        );
        networkError.isNetworkError = true;
        networkError.code = error.code;
        networkError.originalMessage = error.message;
        throw networkError;
      }

      // Enhanced error logging
      if (
        url === API_ROUTES.SEND_RESET_PIN_OTP ||
        url === API_ROUTES.VERIFY_RESET_PIN_OTP
      ) {
      } else if (process.env.NODE_ENV === "development") {
      }
      throw error;
    }

    if (response.data) {
      try {
        // Server public key is returned as plain JSON (PUBLIC_KEY, TYPE, VERSION_CODE).
        if (isServerPublicKeyEndpoint(url)) {
          return response;
        }

        // Plain or wrapped MESSAGES (object or array) — no RSA decryption
        if (response.data.MESSAGES != null) {
          const decrypted = await decryptResponseMessages(response.data);
          return {
            ...response,
            data: decrypted,
            decryptedData: decrypted,
          };
        }

        if (
          response.data.body &&
          typeof response.data.body === "string" &&
          response.data.body.length > 50
        ) {
          if (
            url === API_ROUTES.SEND_RESET_PIN_OTP ||
            url === API_ROUTES.VERIFY_RESET_PIN_OTP
          ) {
          }
          const decryptedData = await decryptResponse(response.data.body);
          if (
            url === API_ROUTES.SEND_RESET_PIN_OTP ||
            url === API_ROUTES.VERIFY_RESET_PIN_OTP
          ) {
            // Response logged
          }
          return {
            ...response,
            data: {
              ...response.data,
              body: decryptedData,
            },
          };
        } else if (
          typeof response.data === "string" &&
          response.data.length > 50
        ) {
          if (
            url === API_ROUTES.SEND_RESET_PIN_OTP ||
            url === API_ROUTES.VERIFY_RESET_PIN_OTP
          ) {
            // Response logged
          }
          const decryptedData = await decryptResponse(response.data);
          if (
            url === API_ROUTES.SEND_RESET_PIN_OTP ||
            url === API_ROUTES.VERIFY_RESET_PIN_OTP
          ) {
            // Response logged
          }
          return {
            ...response,
            data: decryptedData,
          };
        } else if (
          response.data.data &&
          typeof response.data.data === "string" &&
          response.data.data.length > 50
        ) {
          if (
            url === API_ROUTES.SEND_RESET_PIN_OTP ||
            url === API_ROUTES.VERIFY_RESET_PIN_OTP
          ) {
          }
          const decryptedData = await decryptResponse(response.data.data);
          if (
            url === API_ROUTES.SEND_RESET_PIN_OTP ||
            url === API_ROUTES.VERIFY_RESET_PIN_OTP
          ) {
            // Response logged
          }
          return {
            ...response,
            data: {
              ...response.data,
              data: decryptedData,
            },
          };
        } else if (
          response.data.encrypted &&
          typeof response.data.encrypted === "string" &&
          response.data.encrypted.length > 50
        ) {
          if (
            url === API_ROUTES.SEND_RESET_PIN_OTP ||
            url === API_ROUTES.VERIFY_RESET_PIN_OTP
          ) {
          }
          const decryptedData = await decryptResponse(response.data.encrypted);
          if (
            url === API_ROUTES.SEND_RESET_PIN_OTP ||
            url === API_ROUTES.VERIFY_RESET_PIN_OTP
          ) {
            // Response logged
          }
          return {
            ...response,
            data: {
              ...response.data,
              encrypted: decryptedData,
            },
          };
        } else if (
          response.data.message &&
          typeof response.data.message === "string" &&
          response.data.message.length > 50
        ) {
          if (
            url === API_ROUTES.SEND_RESET_PIN_OTP ||
            url === API_ROUTES.VERIFY_RESET_PIN_OTP
          ) {
          }
          const decryptedData = await decryptResponse(response.data.message);
          if (
            url === API_ROUTES.SEND_RESET_PIN_OTP ||
            url === API_ROUTES.VERIFY_RESET_PIN_OTP
          ) {
            // Response logged
          }
          return {
            ...response,
            data: {
              ...response.data,
              message: decryptedData,
            },
          };
        } else if (
          response.data.result &&
          typeof response.data.result === "string" &&
          response.data.result.length > 50
        ) {
          if (
            url === API_ROUTES.SEND_RESET_PIN_OTP ||
            url === API_ROUTES.VERIFY_RESET_PIN_OTP
          ) {
          }
          const decryptedData = await decryptResponse(response.data.result);
          if (
            url === API_ROUTES.SEND_RESET_PIN_OTP ||
            url === API_ROUTES.VERIFY_RESET_PIN_OTP
          ) {
            // Response logged
          }
          return {
            ...response,
            data: {
              ...response.data,
              result: decryptedData,
            },
          };
        } else {
          if (
            url === API_ROUTES.SEND_RESET_PIN_OTP ||
            url === API_ROUTES.VERIFY_RESET_PIN_OTP
          ) {
            // Response logged
          }
        }
        return response;
      } catch (decryptError) {
        if (
          url === API_ROUTES.SEND_RESET_PIN_OTP ||
          url === API_ROUTES.VERIFY_RESET_PIN_OTP
        ) {
        }
        return response;
      }
    }

    if (
      url === API_ROUTES.SEND_RESET_PIN_OTP ||
      url === API_ROUTES.VERIFY_RESET_PIN_OTP
    ) {
    }

    return response;
  } catch (error) {
    // Re-throw network / other errors without clearing the session.
    // Logout only happens for critical session errors elsewhere.
    throw error;
  }
};

const logServerPublicKey = (step, payload) => {
  if (process.env.NODE_ENV === "production") return;
  console.log(`[fetchAndStoreServerPublicKey] ${step}`, payload);
};

const fetchAndStoreServerPublicKey = async (options = {}) => {
  const { baseURL = APIURI.replace(/\/$/, "") } = options;

  try {
    const storageKey = rsaController.STORAGE_KEYS.SERVER_PUBLIC_KEY;
    const storedKey = sessionStorage.getItem(storageKey);

    logServerPublicKey("start", {
      baseURL,
      url: API_ROUTES.SERVER_PUBLIC_KEY,
      hasCachedKeyInSession: Boolean(storedKey),
      cachedKeyLength: storedKey?.length ?? 0,
    });

    if (storedKey) {
      try {
        const serverPublicKeyPEM = await rsaController.getServerPublicKeyPEM();
        logServerPublicKey("cache-hit", {
          source: "sessionStorage",
          keyLength: serverPublicKeyPEM?.length ?? 0,
          keyPreview: serverPublicKeyPEM?.slice(0, 40),
        });
        return serverPublicKeyPEM;
      } catch (loadError) {
        logServerPublicKey("cache-load-failed", {
          message: loadError?.message,
          willFetchFromServer: true,
        });
      }
    }

    const requestOptions = {
      mainHeaders: {},
      baseURL,
      timeout: 20000,
    };
    logServerPublicKey("request", {
      method: "POST",
      url: API_ROUTES.SERVER_PUBLIC_KEY,
      body: "{ PUBLIC_KEY: <user key>, TYPE: '0', VERSION_CODE: '0.5.9' }",
      options: requestOptions,
    });

    let response;
    try {
      response = await postDataToServer(
        API_ROUTES.SERVER_PUBLIC_KEY,
        {},
        requestOptions,
      );
    } catch (fetchError) {
      logServerPublicKey("request-failed", {
        message: fetchError?.message,
        status: fetchError?.response?.status,
        responseData: fetchError?.response?.data,
      });
      throw fetchError;
    }

    const data = response?.data;
    logServerPublicKey("response", {
      httpStatus: response?.status,
      dataType: typeof data,
      topLevelKeys: data && typeof data === "object" ? Object.keys(data) : [],
      TYPE: data?.TYPE,
      VERSION_CODE: data?.VERSION_CODE,
      hasMESSAGES: Boolean(data?.MESSAGES),
      messagesShape: data?.MESSAGES
        ? Array.isArray(data.MESSAGES)
          ? "array"
          : typeof data.MESSAGES
        : null,
      messagesCount: Array.isArray(data?.MESSAGES) ? data.MESSAGES.length : 0,
      messagesObjectKeys:
        data?.MESSAGES &&
        typeof data.MESSAGES === "object" &&
        !Array.isArray(data.MESSAGES)
          ? Object.keys(data.MESSAGES)
          : [],
      hasTopLevelPublicKey: Boolean(data?.PUBLIC_KEY || data?.publicKey),
      publicKeyLength: (data?.PUBLIC_KEY || data?.publicKey || "")?.length ?? 0,
      rawData: data,
    });

    const extracted = data ? await decryptResponseMessages(data) : null;
    const payload =
      extracted && typeof extracted === "object" && !Array.isArray(extracted)
        ? extracted
        : data;

    const rawKey =
      payload?.PUBLIC_KEY ||
      payload?.publicKey ||
      data?.PUBLIC_KEY ||
      data?.publicKey ||
      data?.MESSAGES?.PUBLIC_KEY ||
      data?.MESSAGES?.publicKey ||
      data?.body?.PUBLIC_KEY ||
      data?.body?.publicKey ||
      data?.data?.PUBLIC_KEY ||
      data?.data?.publicKey ||
      (typeof extracted === "string" && extracted.includes("PUBLIC KEY")
        ? extracted
        : null);

    logServerPublicKey("process-key", {
      messagesShape: data?.MESSAGES
        ? Array.isArray(data.MESSAGES)
          ? "array"
          : "object"
        : "none",
      extractedKeys:
        payload && typeof payload === "object" ? Object.keys(payload) : [],
      code: payload?.CODE ?? data?.MESSAGES?.CODE,
      found: Boolean(rawKey),
      keyLength: rawKey?.length ?? 0,
    });

    if (rawKey) {
      await rsaController.setServerPublicKeyFromResponse(rawKey, "pem");
      logServerPublicKey("success", {
        path: data?.MESSAGES?.PUBLIC_KEY || payload?.PUBLIC_KEY
          ? "MESSAGES-object-or-extracted"
          : "top-level",
        storedKeyLength: rawKey.length,
      });
      return rawKey;
    }

    logServerPublicKey("failure", {
      reason: "PUBLIC_KEY not found in plain response or MESSAGES",
      data,
    });
    throw new Error("Server public key not found in response");
  } catch (error) {
    logServerPublicKey("error", {
      message: error?.message,
      stack: error?.stack,
    });
    throw error;
  }
};

// Function to check agent trading account
// This endpoint requires PUBLIC_KEY in the request body along with encrypted MESSAGES
const checkAgentTradingAccount = async (brokerId, options = {}) => {
  const { baseURL = "" } = options;

  try {
    // Always get secret key from environment and hash it (never use provided secretKey directly)
    const extraInfo = await buildExtraInfo();
    const publicKey = await getUserPublicKey();

    // Prepare the data to encrypt (HEADERS and BODY)
    const dataToEncrypt = {
      HEADERS: extraInfo,
      BODY: {
        REFERAL_BROKER: brokerId.toString(),
      },
    };

    // Encrypt the data (returns array if data is large, or single string if small)
    const encryptedResult = await encryptData(dataToEncrypt);
    const messagesArray = Array.isArray(encryptedResult)
      ? encryptedResult
      : [encryptedResult];

    // This endpoint requires PUBLIC_KEY in the body (unlike other endpoints)
    const requestBody = {
      PUBLIC_KEY: publicKey,
      MESSAGES: messagesArray,
      TYPE: "0",
      VERSION_CODE: "0.5.9",
    };

    const fullUrl = baseURL
      ? `${baseURL}/v1/user/check-agent-trading-account`
      : API_ROUTES.CHECK_AGENT_TRADING_ACCOUNT;

    const headers = {
      "Content-Type": "application/json; charset=UTF-8",
    };

    const response = await axios.post(fullUrl, requestBody, { headers });

    // Try to decrypt the response
    const decryptedResponse = await resolveResponseDecryptedData(response);
    return {
      ...response,
      decryptedData: decryptedResponse || response.data,
    };
  } catch (error) {
    throw error;
  }
};

// Function to verify email for demo account
const verifyEmailDemoAccount = async (
  email,
  firstName,
  lastName,
  type,
  hasGreenCard,
  accountType,
  options = {},
) => {
  const { baseURL = "" } = options;

  try {
    // Always get secret key from environment and hash it (never use provided secretKey directly)
    const extraInfo = await buildExtraInfo();
    const publicKey = await getUserPublicKey();

    // Prepare the data to encrypt (HEADERS and BODY)
    const dataToEncrypt = {
      HEADERS: extraInfo,
      BODY: {
        EMAIL: email,
        F_NAME: firstName,
        L_NAME: lastName,
        TYPE: typeof type === "string" ? parseInt(type) || 0 : type || 0,
        HAS_GREEN_CARD: hasGreenCard ? 1 : 0,
        ACCOUNT_TYPE:
          typeof accountType === "string"
            ? parseInt(accountType) || 0
            : accountType || 0,
      },
    };

    // Encrypt the data
    const encryptedResult = await encryptData(dataToEncrypt);
    const messagesArray = Array.isArray(encryptedResult)
      ? encryptedResult
      : [encryptedResult];

    // This endpoint requires PUBLIC_KEY in the body
    const requestBody = {
      PUBLIC_KEY: publicKey,
      MESSAGES: messagesArray,
      TYPE: "0",
      VERSION_CODE: "0.5.9",
    };

    const fullUrl = baseURL
      ? `${baseURL}/v1/user/verify-email-demo-account`
      : API_ROUTES.VERIFY_EMAIL_DEMO_ACCOUNT;

    const headers = {
      "Content-Type": "application/json; charset=UTF-8",
    };

    let response;
    try {
      response = await axios.post(fullUrl, requestBody, { headers });
    } catch (axiosError) {
      // Handle axios errors (4xx, 5xx responses)
      if (axiosError.response) {
        // Server responded with error status, but response might be encrypted
        response = axiosError.response;
      } else {
        // Network error or other axios error
        throw axiosError;
      }
    }

    // Decrypt response
    const decryptedResponse = await resolveResponseDecryptedData(response);
    return {
      ...response,
      decryptedData: decryptedResponse || response.data,
    };
  } catch (error) {
    throw error;
  }
};

// Function to verify OTP for demo account
const verifyOTPDemoAccount = async (
  email,
  accountType,
  verificationCode,
  options = {},
) => {
  const { baseURL = "" } = options;

  try {
    // Always get secret key from environment and hash it (never use provided secretKey directly)
    const extraInfo = await buildExtraInfo();
    const publicKey = await getUserPublicKey();

    // Prepare the data to encrypt (HEADERS and BODY)
    const dataToEncrypt = {
      HEADERS: extraInfo,
      BODY: {
        EMAIL: email,
        ACCOUNT_TYPE:
          typeof accountType === "string"
            ? parseInt(accountType) || 0
            : accountType || 0,
        VERIFICATION_CODE: verificationCode.toString(),
      },
    };

    // Encrypt the data
    const encryptedResult = await encryptData(dataToEncrypt);
    const messagesArray = Array.isArray(encryptedResult)
      ? encryptedResult
      : [encryptedResult];

    // This endpoint requires PUBLIC_KEY in the body
    const requestBody = {
      PUBLIC_KEY: publicKey,
      MESSAGES: messagesArray,
      TYPE: "0",
      VERSION_CODE: "0.5.9",
    };

    const fullUrl = baseURL
      ? `${baseURL}/v1/user/verify-otp-demo-account`
      : API_ROUTES.VERIFY_OTP_DEMO_ACCOUNT;

    const headers = {
      "Content-Type": "application/json; charset=UTF-8",
    };

    const response = await axios.post(fullUrl, requestBody, { headers });

    // Decrypt response
    const decryptedResponse = await resolveResponseDecryptedData(response);
    return {
      ...response,
      decryptedData: decryptedResponse || response.data,
    };
  } catch (error) {
    throw error;
  }
};

const setNewPassword = async (username, password, passwordConfirmation, options = {}) => {
  const { baseURL = '' } = options;

  try {
    const extraInfo = await buildExtraInfo();
    const publicKey = await getUserPublicKey();

    const dataToEncrypt = {
      HEADERS: extraInfo,
      BODY: {
        USERNAME: username,
        PASSWORD: password,
        PASSWORD_CONFIRMATION: passwordConfirmation,
      },
    };

    const encryptedResult = await encryptData(dataToEncrypt);
    const messagesArray = Array.isArray(encryptedResult) ? encryptedResult : [encryptedResult];

    const requestBody = {
      PUBLIC_KEY: publicKey,
      MESSAGES: messagesArray,
      TYPE: "0",
      VERSION_CODE: "0.5.9",
    };

    const fullUrl = baseURL
      ? `${baseURL}/v1/user/set-new-password`
      : API_ROUTES.SET_NEW_PASSWORD;

    const response = await axios.post(fullUrl, requestBody, {
      headers: { 'Content-Type': 'application/json; charset=UTF-8' },
    });

    const decryptedData = await decryptResponseMessages(response.data);

    return {
      ...response,
      decryptedData: decryptedData || response.data,
    };
  } catch (error) {
    throw error;
  }
};

// Function to create user login MT5 account
const createUserLoginMT5Account = async (userInformation, options = {}) => {
  const { baseURL = "" } = options;

  try {
    // Always get secret key from environment and hash it (never use provided secretKey directly)
    const extraInfo = await buildExtraInfo();
    const publicKey = await getUserPublicKey();

    // Prepare the data to encrypt (HEADERS and BODY)
    const dataToEncrypt = {
      HEADERS: extraInfo,
      BODY: {
        USER_INFORMATION: userInformation,
      },
    };

    // Debug: log clear payload before encryption
    // Encrypt the data
    const encryptedResult = await encryptData(dataToEncrypt);
    const messagesArray = Array.isArray(encryptedResult)
      ? encryptedResult
      : [encryptedResult];

    // This endpoint requires PUBLIC_KEY in the body
    const requestBody = {
      PUBLIC_KEY: publicKey,
      MESSAGES: messagesArray,
      TYPE: "0",
      VERSION_CODE: "0.5.9",
    };

    const fullUrl = baseURL
      ? `${baseURL}/v1/user/create-user-login-mt5-account`
      : API_ROUTES.CREATE_USER_LOGIN_MT5_ACCOUNT;

    const headers = {
      "Content-Type": "application/json; charset=UTF-8",
    };

    const response = await axios.post(fullUrl, requestBody, { headers });

    // Decrypt response
    const decryptedResponse = await resolveResponseDecryptedData(response);
    return {
      ...response,
      decryptedData: decryptedResponse || response.data,
    };
  } catch (error) {
    throw error;
  }
};

const createUrbanInfoMT5Account = async (userInformation, options = {}) => {
  const { baseURL = "" } = options;

  try {
    // Always get secret key from environment and hash it (never use provided secretKey directly)
    const extraInfo = await buildExtraInfo();
    const publicKey = await getUserPublicKey();

    // Prepare the data to encrypt (HEADERS and BODY)
    // Backend expects USER_INFORMATION directly, not wrapped with EMAIL at top level
    // The backend controller does: $userInformation = $request->input('USER_INFORMATION')
    const dataToEncrypt = {
      HEADERS: extraInfo,
      BODY: {
        USER_INFORMATION: userInformation,
      },
    };

    // Log the data structure before encryption (for debugging)
    // Log data types to ensure they match backend expectations
    // Encrypt the data
    const encryptedResult = await encryptData(dataToEncrypt);
    const messagesArray = Array.isArray(encryptedResult)
      ? encryptedResult
      : [encryptedResult];

    // This endpoint requires PUBLIC_KEY in the body
    const requestBody = {
      PUBLIC_KEY: publicKey,
      MESSAGES: messagesArray,
      TYPE: "0",
      VERSION_CODE: "0.5.9",
    };

    const fullUrl = baseURL
      ? `${baseURL}/v1/user/create-urban-info-mt5-account`
      : API_ROUTES.CREATE_URBAN_INFO_MT5_ACCOUNT;

    const headers = {
      "Content-Type": "application/json; charset=UTF-8",
    };

    let response;
    try {
      response = await axios.post(fullUrl, requestBody, { headers });
    } catch (axiosError) {
      // Handle axios errors (4xx, 5xx responses)
      if (axiosError.response) {
        response = axiosError.response;
      } else {
        throw axiosError;
      }
    }

    // Decrypt response
    const decryptedResponse = await resolveResponseDecryptedData(response);
    return {
      ...response,
      decryptedData: decryptedResponse || response.data,
    };
  } catch (error) {
    throw error;
  }
};

const createRegulationCompanyMT5Account = async (
  userRegulationData,
  options = {},
) => {
  const { baseURL = "" } = options;

  try {
    // Always get secret key from environment and hash it (never use provided secretKey directly)
    const extraInfo = await buildExtraInfo();
    const publicKey = await getUserPublicKey();

    // Prepare the data to encrypt (HEADERS and BODY)
    const dataToEncrypt = {
      HEADERS: extraInfo,
      BODY: userRegulationData,
    };

    // Encrypt the data
    const encryptedResult = await encryptData(dataToEncrypt);
    const messagesArray = Array.isArray(encryptedResult)
      ? encryptedResult
      : [encryptedResult];

    // This endpoint requires PUBLIC_KEY in the body
    const requestBody = {
      PUBLIC_KEY: publicKey,
      MESSAGES: messagesArray,
      TYPE: "0",
      VERSION_CODE: "0.5.9",
    };

    const fullUrl = baseURL
      ? `${baseURL}/v1/user/create-regulation-company-mt5-account`
      : API_ROUTES.CREATE_REGULATION_COMPANY_MT5_ACCOUNT;

    // Get authentication token from storage (matches Flutter: Authorization: Bearer {token})
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");

    const headers = {
      "Content-Type": "application/json; charset=UTF-8",
      // Add Authorization header if token exists (matches Flutter behavior)
      ...(token && token !== "FIRST_TIME_LOGIN"
        ? { Authorization: `Bearer ${token}` }
        : {}),
    };

    let response;
    try {
      response = await axios.post(fullUrl, requestBody, { headers });
    } catch (axiosError) {
      // Handle axios errors (4xx, 5xx responses)
      if (axiosError.response) {
        response = axiosError.response;
      } else {
        throw axiosError;
      }
    }

    // Decrypt response
    const decryptedResponse = await resolveResponseDecryptedData(response);
    return {
      ...response,
      decryptedData: decryptedResponse || response.data,
    };
  } catch (error) {
    throw error;
  }
};

// Function to create user documents (with file uploads) - matches Flutter's postDataAndImage
const createUserDocumentsMT5Account = async (
  email,
  documentType,
  documentImageFile,
  options = {},
) => {
  const {
    baseURL = "",
    formDataFieldName = "ADDRESS_IMAGE", // Allow custom field name for FormData
  } = options;

  try {
    // Always get secret key from environment and hash it
    const extraInfo = await buildExtraInfo();
    const publicKey = await getUserPublicKey();

    // Prepare imageData map (matches Flutter structure)
    const imageData = {};
    if (documentImageFile) {
      imageData[formDataFieldName] = documentImageFile;
    }

    // Prepare data structure (matches Flutter exactly)
    // Note: Flutter sends additional USER_INFORMATION fields, but at urban info step
    // we may not have all of them yet. EMAIL should be sufficient for document upload.
    const data = {
      EMAIL: email,
      USER_INFORMATION: {
        EMAIL: email,
        // Flutter also sends REGISTER_NUMBER, REGISTER_PLACE, OTHER_NATIONALITY, USER_OCCUPATION
        // but these may not be available at urban info step - backend should accept EMAIL only
      },
      USER_DOCUMENTS: {
        // Match Flutter's structure exactly - use enum values, not null
        SELFIE_IMAGE: UserDocumentSubType.SELFIE,
        FRONT_ID_IMAGE: null, // Not sending ID in urban info step
        BACK_ID_IMAGE: "", // Flutter uses empty string if null (not null)
        ADDRESS_IMAGE:
          documentType !== null && documentType !== undefined
            ? typeof documentType === "number"
              ? documentType
              : typeof documentType === "object" &&
                  documentType?.value !== undefined
                ? documentType.value
                : parseInt(documentType, 10)
            : null,
        SHAREHOLDER_DOCUMENT_TYPE: null,
        DIRECTOR_DOCUMENT_TYPE: null,
        BENIFICAL_DOCUMENT_TYPE: UserDocumentSubType.ID_CARD,
        SIGNATURE_DOCUMENT_TYPE: UserDocumentSubType.SIGNATURE,
        SIGNATURE_VIDEO_TYPE: UserDocumentSubType.SIGNATURE_VIDEO,
        SIGNATURE_CHECK_TYPE: UserDocumentSubType.CHECK_SIGNATURE,
      },
      // IS_CREATE: 0 for generic document uploads (urban info, company); identity verification uses 1 in its own API
      IS_CREATE: 0,
    };

    // Encrypt the data
    const dataToEncrypt = {
      HEADERS: extraInfo,
      BODY: data,
    };

    const encryptedResult = await encryptData(dataToEncrypt);
    const messagesArray = Array.isArray(encryptedResult)
      ? encryptedResult
      : [encryptedResult];

    const fullUrl = baseURL
      ? `${baseURL}/v1/user/create-user-documents-mt5-account`
      : API_ROUTES.CREATE_USER_DOCUMENTS_MT5_ACCOUNT;

    // Create FormData for multipart/form-data request (matches Flutter's postDataAndImage)
    const formData = new FormData();

    // Add encrypted MESSAGES as JSON string (Flutter uses jsonEncode)
    // Note: Flutter sends MESSAGES as a JSON-encoded string in form fields
    formData.append("MESSAGES", JSON.stringify(messagesArray));
    formData.append("PUBLIC_KEY", publicKey);
    formData.append("TYPE", "0");
    formData.append("VERSION_CODE", "0.5.9");

    // Add image files
    for (const [key, file] of Object.entries(imageData)) {
      formData.append(key, file, file.name || "image.jpg");
    }

    const headers = {
      // Don't set Content-Type - browser will set it with boundary for FormData
    };

    let response;
    try {
      response = await axios.post(fullUrl, formData, { headers });
    } catch (axiosError) {
      if (axiosError.response) {
        response = axiosError.response;
      } else {
        throw axiosError;
      }
    }

    const decryptedResponse = await resolveResponseDecryptedData(response);

    return {
      ...response,
      decryptedData: decryptedResponse || response.data,
    };
  } catch (error) {
    throw error;
  }
};

// Function to get agreements list
const getAgreements = async (options = {}) => {
  const { baseURL = "" } = options;

  try {
    const fullUrl = baseURL
      ? `${baseURL}/v1/user/get-agreements`
      : API_ROUTES.GET_AGREEMENTS;

    // Build device info
    const extraInfo = await buildExtraInfo();

    // Build request body - empty object as per Flutter implementation
    const bodyData = {};

    // Encrypt the request data (HEADERS + BODY)
    const dataToEncrypt = {
      HEADERS: extraInfo,
      BODY: bodyData,
    };

    const encryptedMessages = await encryptData(dataToEncrypt);

    // Get user public key
    const publicKey = await getUserPublicKey();

    // Build final request body with encryption
    const requestBody = {
      MESSAGES: Array.isArray(encryptedMessages)
        ? encryptedMessages
        : [encryptedMessages],
      PUBLIC_KEY: publicKey,
      TYPE: "0",
      VERSION_CODE: "1.0.0", // You may want to get this from package.json or env
    };

    const headers = {
      "Content-Type": "application/json; charset=UTF-8",
    };

    let response;
    try {
      response = await axios.post(fullUrl, requestBody, { headers });
    } catch (axiosError) {
      if (axiosError.response) {
        response = axiosError.response;
      } else {
        throw axiosError;
      }
    }

    // Handle response - may be encrypted or plain JSON
    let agreementsData = null;
    let responseCode = null;

    const agreementsParsed = await decryptResponseMessages(response.data);
    if (agreementsParsed && typeof agreementsParsed === "object") {
      responseCode = agreementsParsed.CODE ?? agreementsParsed.code;
      agreementsData = agreementsParsed.DATA ?? agreementsParsed.data ?? [];
    } else if (response.data) {
      responseCode = response.data.CODE;
      agreementsData = response.data.DATA || [];
    }

    return {
      ...response,
      data: {
        CODE: responseCode,
        DATA: agreementsData || [],
      },
      agreements: agreementsData || [],
    };
  } catch (error) {
    throw error;
  }
};

const getFaq = async (language = "en", options = {}) => {
  const body = {
    LANGUAGE: (language || "en").toLowerCase(),
  };
  return postEncryptedJson(API_ROUTES.GET_FAQ, body, options);
};

const getTutorialInfo = async (language = "en", options = {}) => {
  const userId =
    localStorage.getItem("userId") || sessionStorage.getItem("userId") || null;

  const payloads = [
    {},
    { LANGUAGE: (language || "en").toLowerCase() },
    ...(userId ? [{ USER_ID: String(userId) }] : []),
    ...(userId
      ? [
          {
            LANGUAGE: (language || "en").toLowerCase(),
            USER_ID: String(userId),
          },
        ]
      : []),
  ];

  let lastResponse = null;
  let lastError = null;

  for (const payload of payloads) {
    try {
      const response = await postEncryptedJson(
        API_ROUTES.GET_TUTORIAL_INFO,
        payload,
        options,
      );
      const data = response?.decryptedData || response?.data || {};
      const code = Number(
        data?.CODE ??
          data?.code ??
          data?.BODY?.CODE ??
          data?.body?.CODE ??
          data?.body?.code,
      );

      // Return first successful payload.
      if (code === AppConstants.responseOk) {
        return response;
      }

      lastResponse = response;
    } catch (error) {
      lastError = error;
    }
  }

  if (lastResponse) return lastResponse;
  if (lastError) throw lastError;
  return postEncryptedJson(API_ROUTES.GET_TUTORIAL_INFO, {}, options);
};

const getAppUrl = async (options = {}) => {
  const runGetAppUrlWithDeviceOs = async (deviceOsOverride = null) => {
    const extraInfo = await buildExtraInfo();
    if (deviceOsOverride) {
      extraInfo["device-os"] = deviceOsOverride;
    }

    const dataToEncrypt = {
      HEADERS: extraInfo,
      BODY: {},
    };
    const encryptedMessages = await encryptData(dataToEncrypt);
    const publicKey = await getUserPublicKey();
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");

    const headers = {
      "Content-Type": "application/json; charset=UTF-8",
      ...(token && token !== "FIRST_TIME_LOGIN"
        ? { Authorization: `Bearer ${token}` }
        : {}),
      ...(deviceOsOverride ? { "device-os": deviceOsOverride } : {}),
    };

    return axios.post(
      API_ROUTES.GET_APP_URL,
      {
        MESSAGES: Array.isArray(encryptedMessages)
          ? encryptedMessages
          : [encryptedMessages],
        PUBLIC_KEY: publicKey,
        TYPE: "0",
        VERSION_CODE: "0.5.9",
      },
      { headers },
    );
  };

  try {
    const primary = await postEncryptedJson(
      API_ROUTES.GET_APP_URL,
      {},
      options,
    );
    const primaryData = primary?.decryptedData || primary?.data || {};
    if (Number(primaryData?.CODE) === AppConstants.responseOk) {
      return primary;
    }
  } catch (_) {
    // fall through to mobile OS fallback attempts
  }

  const fallbackOrder = ["android", "ios"];
  let lastResponse = null;
  let lastError = null;

  for (const os of fallbackOrder) {
    try {
      const response = await runGetAppUrlWithDeviceOs(os);
      let decryptedData = response?.data || {};
      try {
        if (response?.data?.MESSAGES && Array.isArray(response.data.MESSAGES)) {
          decryptedData =
            (await decryptResponseMessages(response.data)) || decryptedData;
        }
      } catch (_) {}

      const wrapped = {
        ...response,
        decryptedData,
      };
      if (Number(decryptedData?.CODE) === AppConstants.responseOk) {
        return wrapped;
      }
      lastResponse = wrapped;
    } catch (error) {
      lastError = error;
    }
  }

  if (lastResponse) return lastResponse;
  if (lastError) throw lastError;
  return postEncryptedJson(API_ROUTES.GET_APP_URL, {}, options);
};

// Function to get HTML page content
const getHtmlPage = async (language, key, options = {}) => {
  const {
    baseURL = "",
    email = null, // Email for user identification during registration (when no token available)
    userId = null, // Optional user id for agreement placeholders/lookup
  } = options;

  try {
    const fullUrl = baseURL
      ? `${baseURL}/v1/user/get-html-page`
      : API_ROUTES.GET_HTML_PAGE;

    // Build device info
    const extraInfo = await buildExtraInfo();

    // Build request body with LANGUAGE and KEY
    // Include EMAIL if provided (for registration flow when no token is available)
    const bodyData = {
      LANGUAGE: language || "en", // Default to 'en' if not provided
      KEY: key || "", // KEY is required - should be the agreement KEY
    };
    
    
    
    // Encrypt the request data (HEADERS + BODY)
    const dataToEncrypt = {
      HEADERS: extraInfo,
      BODY: bodyData,
    };

    const encryptedMessages = await encryptData(dataToEncrypt);

    // Get user public key
    const publicKey = await getUserPublicKey();

    // Build final request body with encryption
    const requestBody = {
      MESSAGES: Array.isArray(encryptedMessages)
        ? encryptedMessages
        : [encryptedMessages],
      PUBLIC_KEY: publicKey,
      TYPE: "0",
      VERSION_CODE: "0.5.9",
    };

    // Get authentication token from storage (matches Flutter: Authorization: Bearer {token})
    // Flutter sends: 'Authorization': 'Bearer $token' (unless token == AppConstants.FIRST_TIME_LOGIN)
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");

    const headers = {
      "Content-Type": "application/json; charset=UTF-8",
      // Add Authorization header if token exists (matches Flutter behavior)
      ...(token && token !== "FIRST_TIME_LOGIN"
        ? { Authorization: `Bearer ${token}` }
        : {}),
    };

    let response;
    try {
      response = await axios.post(fullUrl, requestBody, { headers });
    } catch (axiosError) {
      if (axiosError.response) {
        response = axiosError.response;
      } else {
        throw axiosError;
      }
    }

    // Handle response - may be encrypted or plain JSON
    let htmlContent = null;
    let responseCode = null;

    const htmlParsed = await decryptResponseMessages(response.data);
    if (htmlParsed && typeof htmlParsed === "object" && !Array.isArray(htmlParsed)) {
      responseCode = htmlParsed.CODE ?? htmlParsed.code;
      htmlContent =
        htmlParsed.DATA ?? htmlParsed.HTML ?? htmlParsed.CONTENT ?? null;
      if (typeof htmlContent === "string") {
        htmlContent = htmlContent
          .replace(/\\n/g, "\n")
          .replace(/\\r/g, "\r")
          .replace(/\\t/g, "\t")
          .replace(/\\"/g, '"')
          .replace(/\\\\/g, "\\");
      }
    } else if (response.data) {
      responseCode = response.data.CODE;
      htmlContent =
        response.data.DATA ||
        response.data.HTML ||
        response.data.CONTENT ||
        null;
    }

    return {
      ...response,
      data: {
        CODE: responseCode,
        DATA: htmlContent,
        HTML: htmlContent,
      },
      htmlContent: htmlContent,
    };
  } catch (error) {
    throw error;
  }
};

// Function to get user profile (returns user_id in DATA.ID)
const getProfile = async (options = {}) => {
  const {
    baseURL = "",
    withDoc = 1, // Default to 1 to include documents (matches Flutter)
  } = options;

  try {
    // Always get secret key from environment and hash it
    const extraInfo = await buildExtraInfo();
    const publicKey = await getUserPublicKey();

    // Prepare the data to encrypt (HEADERS and BODY)
    const dataToEncrypt = {
      HEADERS: extraInfo,
      BODY: {
        WITH_DOC: withDoc,
      },
    };

    // Encrypt the data
    const encryptedResult = await encryptData(dataToEncrypt);
    const messagesArray = Array.isArray(encryptedResult)
      ? encryptedResult
      : [encryptedResult];

    // This endpoint requires PUBLIC_KEY in the body
    const requestBody = {
      PUBLIC_KEY: publicKey,
      MESSAGES: messagesArray,
      TYPE: "0",
      VERSION_CODE: "0.5.9",
    };

    const fullUrl = baseURL
      ? `${baseURL}/v1/user/get-profile`
      : API_ROUTES.GET_PROFILE;

    const headers = {
      "Content-Type": "application/json; charset=UTF-8",
    };

    let response;
    try {
      response = await axios.post(fullUrl, requestBody, { headers });
    } catch (axiosError) {
      if (axiosError.response) {
        response = axiosError.response;
      } else {
        throw axiosError;
      }
    }

    // Decrypt response
    const decryptedResponse = await resolveResponseDecryptedData(response);
    return {
      ...response,
      decryptedData: decryptedResponse || response.data,
    };
  } catch (error) {
    throw error;
  }
};

const updateProfile = async (data, options = {}) => {
  return postEncryptedJson(API_ROUTES.UPDATE_PERSONAL_PROFILE, data, options);
};

const updateProfileStatus = async (options = {}) => {
  return postEncryptedJson(API_ROUTES.UPDATE_PROFILE_STATUS, {}, options);
};

const updatePersonalInformation = async (data, options = {}) => {
  return postEncryptedJson(
    API_ROUTES.UPDATE_PERSONAL_INFORMATION,
    data,
    options,
  );
};

const HOME_PAGE_INFOS_CACHE_KEY = "homePageInfosCacheV1";
const HOME_PAGE_INFOS_CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes
let homePageInfosMemoryCache = null;
const homePageInfosInFlightPromises = new Map();

const isHomePageInfoCacheValid = (cacheEntry) => {
  if (!cacheEntry || typeof cacheEntry !== "object") return false;
  if (typeof cacheEntry.timestamp !== "number") return false;
  return Date.now() - cacheEntry.timestamp < HOME_PAGE_INFOS_CACHE_TTL_MS;
};

/** Short stable fingerprint so cached home-page payloads are never shared across users/tokens. */
const homePageInfosAuthFingerprint = (token) => {
  if (!token || token === "FIRST_TIME_LOGIN") return "";
  const t = String(token);
  if (t.length < 16) return t;
  return `${t.length}:${t.slice(0, 12)}:${t.slice(-16)}`;
};

const getHomePageInfosRequestKey = ({
  baseURL = "",
  body = {},
  authFingerprint = "",
} = {}) => {
  const normalizedBody = body && typeof body === "object" ? body : {};
  return JSON.stringify({
    baseURL: String(baseURL || ""),
    body: normalizedBody,
    auth: String(authFingerprint || ""),
  });
};

const readHomePageInfosCacheStore = () => {
  if (
    homePageInfosMemoryCache &&
    typeof homePageInfosMemoryCache === "object"
  ) {
    return homePageInfosMemoryCache;
  }

  try {
    const raw = sessionStorage.getItem(HOME_PAGE_INFOS_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    homePageInfosMemoryCache = parsed;
  } catch (_) {
    return {};
  }
  return homePageInfosMemoryCache;
};

const readHomePageInfosCache = (requestKey) => {
  const store = readHomePageInfosCacheStore();
  const entry = store?.[requestKey];
  if (!isHomePageInfoCacheValid(entry)) return null;
  return entry.response;
};

const writeHomePageInfosCache = (requestKey, response) => {
  if (!response) return;
  const store = readHomePageInfosCacheStore();
  const updated = {
    ...store,
    [requestKey]: { timestamp: Date.now(), response },
  };
  homePageInfosMemoryCache = updated;
  try {
    sessionStorage.setItem(HOME_PAGE_INFOS_CACHE_KEY, JSON.stringify(updated));
  } catch (_) {}
};

/** Clear home-page cache and mirrored user session copy. Call on logout. */
export const clearHomePageInfosClientCache = () => {
  homePageInfosMemoryCache = null;
  try {
    homePageInfosInFlightPromises.clear();
  } catch (_) {}
  try {
    sessionStorage.removeItem(HOME_PAGE_INFOS_CACHE_KEY);
    sessionStorage.removeItem("userData");
    sessionStorage.removeItem("userDataTimestamp");
  } catch (_) {}
};

const getHomePageInfos = async (options = {}) => {
  const { baseURL = "", body = {}, forceRefresh = false } = options;

  try {
    // Get authentication token from storage
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token || token === "FIRST_TIME_LOGIN") {
      throw new Error("Authentication token is required. Please login first.");
    }

    const requestKey = getHomePageInfosRequestKey({
      baseURL,
      body,
      authFingerprint: homePageInfosAuthFingerprint(token),
    });
    const cacheableRequestShape =
      body && typeof body === "object" && Object.keys(body).length <= 1;
    const shouldUseCache = !forceRefresh && cacheableRequestShape;

    if (shouldUseCache) {
      const cached = readHomePageInfosCache(requestKey);
      if (cached) {
        return cached;
      }
      const inFlight = homePageInfosInFlightPromises.get(requestKey);
      if (inFlight) {
        return await inFlight;
      }
    }

    // Build extra info
    const extraInfo = await buildExtraInfo();

    // Build data to encrypt (matches Flutter: {HEADERS: extraInfo, BODY: {}})
    // GET_HOME_PAGE_INFO is NOT in urlNeedPublicKeys, so no PUBLIC_KEY needed
    const dataToEncrypt = {
      HEADERS: extraInfo,
      BODY: body && typeof body === "object" ? body : {},
    };

    // Encrypt the data (matches Flutter: messagesEncryption({HEADERS: extraInfo, BODY: body}))
    const encryptedMessages = await encryptData(dataToEncrypt);

    // Build request body (matches Flutter: {MESSAGES: encryptedArray})
    const requestBody = {
      MESSAGES: Array.isArray(encryptedMessages)
        ? encryptedMessages
        : [encryptedMessages],
    };

    const fullUrl = baseURL
      ? `${baseURL}/v1/user/get-home-page-infos`
      : API_ROUTES.GET_HOME_PAGE_INFOS;

    // Include Authorization header with Bearer token in the main headers
    const headers = {
      "Content-Type": "application/json; charset=UTF-8",
      Authorization: `Bearer ${token}`,
    };

    // Log request details

    const requestPromise = (async () => {
      let response;
      try {
        response = await axios.post(fullUrl, requestBody, { headers });

        // Log raw response
      } catch (axiosError) {
        if (axiosError.response) {
          response = axiosError.response;
        } else {
          throw axiosError;
        }
      }

      const decryptedResponse = await resolveResponseDecryptedData(response);
      const finalResponse = {
        ...response,
        decryptedData: decryptedResponse || response.data,
      };

      if (
        cacheableRequestShape &&
        finalResponse?.decryptedData?.CODE === AppConstants.responseOk
      ) {
        writeHomePageInfosCache(requestKey, finalResponse);
      }

      return finalResponse;
    })();

    if (shouldUseCache) {
      homePageInfosInFlightPromises.set(requestKey, requestPromise);
      try {
        return await requestPromise;
      } finally {
        homePageInfosInFlightPromises.delete(requestKey);
      }
    }

    return await requestPromise;
  } catch (error) {
    throw error;
  }
};

/**
 * IB dashboard payload (same encryption contract as get-home-page-infos).
 */
const getIbDashboard = async (options = {}) => {
  const { baseURL = "", body = {} } = options;

  try {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token || token === "FIRST_TIME_LOGIN") {
      console.warn(
        "[get-ib-dashboard] missing or invalid token:",
        token || "(empty)",
      );
      throw new Error("Authentication token is required. Please login first.");
    }

    const extraInfo = await buildExtraInfo();

    const dataToEncrypt = {
      HEADERS: extraInfo,
      BODY: body && typeof body === "object" ? body : {},
    };

    const encryptedMessages = await encryptData(dataToEncrypt);

    const requestBody = {
      MESSAGES: Array.isArray(encryptedMessages)
        ? encryptedMessages
        : [encryptedMessages],
    };

    const fullUrl = baseURL
      ? `${baseURL}/v1/user/get-ib-dashboard`
      : API_ROUTES.GET_IB_DASHBOARD;

    const headers = {
      "Content-Type": "application/json; charset=UTF-8",
      Authorization: `Bearer ${token}`,
    };

    let response;
    try {
      response = await axios.post(fullUrl, requestBody, { headers });
    } catch (axiosError) {
      if (axiosError.response) {
        response = axiosError.response;
      } else {
        throw axiosError;
      }
    }

    const decryptedResponse = await resolveResponseDecryptedData(response);
    return {
      ...response,
      decryptedData: decryptedResponse || response.data,
    };
  } catch (error) {
    const tokenOnErr =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    console.error("[get-ib-dashboard] error:", error?.message || error);
    void tokenOnErr;
    throw error;
  }
};

/**
 * User marketing plans payload (same encryption contract as dashboard calls).
 */
const getMarketingPlans = async (options = {}) => {
  const { baseURL = "", body = {} } = options;

  try {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token || token === "FIRST_TIME_LOGIN") {
      throw new Error("Authentication token is required. Please login first.");
    }

    const extraInfo = await buildExtraInfo();
    const dataToEncrypt = {
      HEADERS: extraInfo,
      BODY: body && typeof body === "object" ? body : {},
    };
    const encryptedMessages = await encryptData(dataToEncrypt);
    const requestBody = {
      MESSAGES: Array.isArray(encryptedMessages)
        ? encryptedMessages
        : [encryptedMessages],
    };

    const fullUrl = baseURL
      ? `${baseURL}/v1/user/get-marketing-plans`
      : API_ROUTES.GET_MARKETING_PLANS;
    const headers = {
      "Content-Type": "application/json; charset=UTF-8",
      Authorization: `Bearer ${token}`,
    };

    let response;
    try {
      response = await axios.post(fullUrl, requestBody, { headers });
    } catch (axiosError) {
      if (axiosError.response) {
        response = axiosError.response;
      } else {
        throw axiosError;
      }
    }

    const decryptedResponse = await resolveResponseDecryptedData(response);
    return {
      ...response,
      decryptedData: decryptedResponse || response.data,
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Broker tools list (same encryption contract as get-marketing-plans / get-ib-dashboard).
 */
const getBrokerTools = async (options = {}) => {
  const { baseURL = "", body = {} } = options;

  try {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token || token === "FIRST_TIME_LOGIN") {
      throw new Error("Authentication token is required. Please login first.");
    }

    const extraInfo = await buildExtraInfo();
    const dataToEncrypt = {
      HEADERS: extraInfo,
      BODY: body && typeof body === "object" ? body : {},
    };
    const encryptedMessages = await encryptData(dataToEncrypt);
    const requestBody = {
      MESSAGES: Array.isArray(encryptedMessages)
        ? encryptedMessages
        : [encryptedMessages],
    };

    const fullUrl = baseURL
      ? `${baseURL}/v1/user/get-broker-tools`
      : API_ROUTES.GET_BROKER_TOOLS;
    const headers = {
      "Content-Type": "application/json; charset=UTF-8",
      Authorization: `Bearer ${token}`,
    };

    let response;
    try {
      response = await axios.post(fullUrl, requestBody, { headers });
    } catch (axiosError) {
      if (axiosError.response) {
        response = axiosError.response;
      } else {
        throw axiosError;
      }
    }

    const decryptedResponse = await resolveResponseDecryptedData(response);
    return {
      ...response,
      decryptedData: decryptedResponse || response.data,
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Fetch a broker tool template preview (image/video/HTML as blob).
 * GET /api/v1/user/get-broker-tool-template/{templateName}
 */
const getBrokerToolTemplate = async (templateName, options = {}) => {
  const safeName = String(templateName || "").trim();
  if (!safeName) {
    throw new Error("Template name is required.");
  }

  const { baseURL = "", original = false, cacheBust = false } = options;
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");
  if (!token || token === "FIRST_TIME_LOGIN") {
    throw new Error("Authentication token is required. Please login first.");
  }

  const extraInfo = await buildExtraInfo();
  const headers = {
    "device-id": extraInfo["device-id"] ?? "",
    "device-model": extraInfo["device-model"] ?? "",
    "device-os": extraInfo["device-os"] ?? "",
    "device-ip": extraInfo["device-ip"] ?? "null",
    "device-position":
      extraInfo["device-position"] ??
      JSON.stringify({ LATITUDE: 0, LONGTITUDE: 0 }),
    Authorization: `Bearer ${token}`,
  };

  const base = baseURL
    ? `${baseURL.replace(/\/$/, "")}/v1/user/get-broker-tool-template`
    : `${APIURI}v1/user/get-broker-tool-template`;
  const params = new URLSearchParams();
  if (original) params.set("original", "1");
  if (cacheBust) params.set("_", String(Date.now()));
  const qs = params.toString();
  const fullUrl = `${base}/${encodeURIComponent(safeName)}${qs ? `?${qs}` : ""}`;

  return axios.get(fullUrl, { headers, responseType: "blob" });
};

/**
 * Fetch a marketing plan attachment (image/video as blob).
 * GET /api/v1/user/get-marketing-plan-attachment/{attachmentName}
 */
const getMarketingPlanAttachment = async (attachmentName, options = {}) => {
  const safeName = String(attachmentName || "").trim();
  if (!safeName) {
    throw new Error("Attachment name is required.");
  }

  const { baseURL = "", cacheBust = false } = options;
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");
  if (!token || token === "FIRST_TIME_LOGIN") {
    throw new Error("Authentication token is required. Please login first.");
  }

  const extraInfo = await buildExtraInfo();
  const headers = {
    "device-id": extraInfo["device-id"] ?? "",
    "device-model": extraInfo["device-model"] ?? "",
    "device-os": extraInfo["device-os"] ?? "",
    "device-ip": extraInfo["device-ip"] ?? "null",
    "device-position":
      extraInfo["device-position"] ??
      JSON.stringify({ LATITUDE: 0, LONGTITUDE: 0 }),
    Authorization: `Bearer ${token}`,
  };

  const base = baseURL
    ? `${baseURL.replace(/\/$/, "")}/v1/user/get-marketing-plan-attachment`
    : `${APIURI}v1/user/get-marketing-plan-attachment`;
  const params = new URLSearchParams();
  if (cacheBust) params.set("_", String(Date.now()));
  const qs = params.toString();
  const fullUrl = `${base}/${encodeURIComponent(safeName)}${qs ? `?${qs}` : ""}`;

  return axios.get(fullUrl, { headers, responseType: "blob" });
};

/**
 * Save IB-customized broker tool image (multipart). Plain JSON response { CODE, DATA }.
 */
const saveBrokerEditingTemplate = async ({
  brokerToolId,
  name,
  phone,
  imageBlob,
  baseURL = "",
} = {}) => {
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");
  if (!token || token === "FIRST_TIME_LOGIN") {
    throw new Error("Authentication token is required. Please login first.");
  }
  if (!brokerToolId || !name || !phone || !imageBlob) {
    throw new Error("Missing required fields for saving template.");
  }

  const extraInfo = await buildExtraInfo();
  const headers = {
    "device-id": extraInfo["device-id"] ?? "",
    "device-model": extraInfo["device-model"] ?? "",
    "device-os": extraInfo["device-os"] ?? "",
    "device-ip": extraInfo["device-ip"] ?? "null",
    "device-position":
      extraInfo["device-position"] ??
      JSON.stringify({ LATITUDE: 0, LONGTITUDE: 0 }),
    Authorization: `Bearer ${token}`,
  };

  const formData = new FormData();
  formData.append("BROKER_TOOL_ID", String(brokerToolId));
  formData.append("NAME", String(name).trim());
  formData.append("PHONE", String(phone).trim());
  const file =
    imageBlob instanceof File
      ? imageBlob
      : new File([imageBlob], "broker-tool-export.png", { type: "image/png" });
  formData.append("IMAGE", file);

  const fullUrl = baseURL
    ? `${baseURL.replace(/\/$/, "")}/v1/user/save-broker-editing-template`
    : API_ROUTES.SAVE_BROKER_EDITING_TEMPLATE;

  return axios.post(fullUrl, formData, { headers });
};

/**
 * Get all e-wallets info (similar to Flutter's updateEWalletsInfo)
 * Returns { ...response, decryptedData } with DATA containing accounts array
 */
const getAllEWalletsInfo = async (options = {}) => {
  const { baseURL = "" } = options;

  try {
    // Get authentication token from storage
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token || token === "FIRST_TIME_LOGIN") {
      throw new Error("Authentication token is required. Please login first.");
    }

    // Build extra info
    const extraInfo = await buildExtraInfo();

    // Build data to encrypt (matches Flutter: {HEADERS: extraInfo, BODY: {}})
    const dataToEncrypt = {
      HEADERS: extraInfo,
      BODY: {}, // Empty body for get-all-ewallets-info
    };

    // Encrypt the data (matches Flutter: messagesEncryption({HEADERS: extraInfo, BODY: body}))
    const encryptedMessages = await encryptData(dataToEncrypt);

    // Build request body (matches Flutter: {MESSAGES: encryptedArray})
    const requestBody = {
      MESSAGES: Array.isArray(encryptedMessages)
        ? encryptedMessages
        : [encryptedMessages],
    };

    const fullUrl = baseURL
      ? `${baseURL}/v1/user/get-all-ewallets-info`
      : API_ROUTES.GET_ALL_EWALLETS_INFO;

    // Include Authorization header with Bearer token in the main headers
    const headers = {
      "Content-Type": "application/json; charset=UTF-8",
      Authorization: `Bearer ${token}`,
    };

    let response;
    try {
      response = await axios.post(fullUrl, requestBody, { headers });
    } catch (axiosError) {
      if (axiosError.response) {
        response = axiosError.response;
      } else {
        throw axiosError;
      }
    }

    const decryptedResponse = await resolveResponseDecryptedData(response);
    return {
      ...response,
      decryptedData: decryptedResponse || response.data,
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Get all e-wallets amount (balance) (similar to Flutter's updateEWalletsAmount)
 * Returns { ...response, decryptedData } with DATA containing wallet amounts array
 */
const getAllEWalletsAmount = async (options = {}) => {
  const { baseURL = "" } = options;

  try {
    // Get authentication token from storage
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token || token === "FIRST_TIME_LOGIN") {
      throw new Error("Authentication token is required. Please login first.");
    }

    // Build extra info
    const extraInfo = await buildExtraInfo();

    // Build data to encrypt (matches Flutter: {HEADERS: extraInfo, BODY: {}})
    const dataToEncrypt = {
      HEADERS: extraInfo,
      BODY: {}, // Empty body for get-all-ewallets-amount
    };

    // Encrypt the data (matches Flutter: messagesEncryption({HEADERS: extraInfo, BODY: body}))
    const encryptedMessages = await encryptData(dataToEncrypt);

    // Build request body (matches Flutter: {MESSAGES: encryptedArray})
    const requestBody = {
      MESSAGES: Array.isArray(encryptedMessages)
        ? encryptedMessages
        : [encryptedMessages],
    };

    const fullUrl = baseURL
      ? `${baseURL}/v1/user/get-all-ewallets-amount`
      : API_ROUTES.GET_ALL_EWALLETS_AMOUNT;

    // Include Authorization header with Bearer token in the main headers
    const headers = {
      "Content-Type": "application/json; charset=UTF-8",
      Authorization: `Bearer ${token}`,
    };

    let response;
    try {
      response = await axios.post(fullUrl, requestBody, { headers });
    } catch (axiosError) {
      if (axiosError.response) {
        response = axiosError.response;
      } else {
        throw axiosError;
      }
    }

    const decryptedResponse = await resolveResponseDecryptedData(response);
    return {
      ...response,
      decryptedData: decryptedResponse || response.data,
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Get chat messages (support chat).
 * Backend endpoint: /v1/user/get-chat-message?page={page}
 * BODY (encrypted): { PER_PAGE }
 */
const getChatMessages = async (page = 1, perPage = 20, options = {}) => {
  // Use postEncryptedJson to match other encrypted endpoints with Bearer token
  const endpoint = `${API_ROUTES.GET_CHAT_MESSAGE}?page=${page}`;
  const body = {
    PER_PAGE: perPage,
  };

  const res = await postEncryptedJson(endpoint, body, options);
  return res;
};

/**
 * Add a new chat message.
 * Backend endpoint: /v1/user/add-chat-message
 * BODY (encrypted): { MESSAGE, USER_ID }
 */
const addChatMessage = async (message, userId = null, options = {}) => {
  if (!message || typeof message !== "string" || !message.trim()) {
    throw new Error("Message text is required.");
  }

  const body = {
    MESSAGE: message.trim(),
    USER_ID: userId,
  };

  const res = await postEncryptedJson(
    API_ROUTES.ADD_CHAT_MESSAGE,
    body,
    options,
  );
  return res;
};

const buildAuthorizedDeviceHeaders = async () => {
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");
  if (!token || token === "FIRST_TIME_LOGIN") {
    throw new Error("Authentication token is required. Please login first.");
  }

  const extraInfo = await buildExtraInfo();
  return {
    Authorization: `Bearer ${token}`,
    "device-id": extraInfo["device-id"] ?? "",
    "device-model": extraInfo["device-model"] ?? "",
    "device-os": extraInfo["device-os"] ?? "",
    "device-ip": extraInfo["device-ip"] ?? "null",
    "device-position":
      extraInfo["device-position"] ??
      JSON.stringify({ LATITUDE: 0, LONGTITUDE: 0 }),
    "user-type": String(extraInfo["user-type"] ?? "0"),
  };
};

/**
 * Upload a support chat document.
 * Backend endpoint: /v1/user/add-chat-document
 * BODY (multipart): DOCUMENT, CHAT_TYPE, MESSAGE_TYPE
 */
const addChatDocument = async (
  file,
  { chatType = 0, messageType = 1 } = {},
  options = {},
) => {
  if (!file) {
    throw new Error("Document file is required.");
  }

  const { baseURL = "" } = options;
  const fullUrl = baseURL
    ? `${baseURL.replace(/\/$/, "")}/v1/user/add-chat-document`
    : API_ROUTES.ADD_CHAT_DOCUMENT;

  const formData = new FormData();
  formData.append("DOCUMENT", file, file.name || "chat-document");
  formData.append("CHAT_TYPE", String(chatType));
  formData.append("MESSAGE_TYPE", String(messageType));

  const headers = await buildAuthorizedDeviceHeaders();
  const response = await axios.post(fullUrl, formData, { headers });
  return response;
};

/**
 * Download a support chat document by filename.
 * Backend endpoint: /v1/user/get-chat-document/{documentName}
 */
const getChatDocument = async (documentName, options = {}) => {
  const safeName = String(documentName || "").trim();
  if (!safeName) {
    throw new Error("Document name is required.");
  }

  const { baseURL = "" } = options;
  const base = baseURL
    ? `${baseURL.replace(/\/$/, "")}/v1/user/get-chat-document`
    : API_ROUTES.GET_CHAT_DOCUMENT;
  const fullUrl = `${base}/${encodeURIComponent(safeName)}`;
  const headers = await buildAuthorizedDeviceHeaders();

  return axios.get(fullUrl, { headers, responseType: "blob" });
};

/**
 * Sign forced agreement with signature image file.
 * Backend endpoint: /v1/user/sign-forced-agreement
 * BODY: FORCED_AGREEMENT_ID + SIGNATURE(file)
 */
const signForcedAgreement = async (
  forcedAgreementId,
  signatureFile,
  options = {},
) => {
  const id = String(forcedAgreementId || "").trim();
  if (!id) {
    throw new Error("FORCED_AGREEMENT_ID is required.");
  }
  if (!signatureFile) {
    throw new Error("Signature file is required.");
  }

  const res = await postEncryptedFormData(
    API_ROUTES.SIGN_FORCED_AGREEMENT,
    { FORCED_AGREEMENT_ID: id },
    { SIGNATURE: signatureFile },
    options,
  );
  return res;
};

// Function to create identity verification with documents and agreements
const createIdentityVerificationWithDocuments = async (
  email,
  documentType,
  identificationNumber,
  imageFiles,
  agreementIds,
  options = {},
) => {
  const {
    baseURL = "",
    userId = null, // user_id from options (if available)
  } = options;

  try {
    // Always get secret key from environment and hash it
    const extraInfo = await buildExtraInfo();
    const publicKey = await getUserPublicKey();

    // Get urban info data for OTHER_NATIONALITY if available
    const savedUrbanInfo = sessionStorage.getItem("urban_info_data");
    let urbanInfoData = null;
    if (savedUrbanInfo) {
      try {
        urbanInfoData = JSON.parse(savedUrbanInfo);
      } catch (e) {
        // Failed to parse
      }
    }

    // Extract document type numeric value (handle both number and object with value property)
    const documentTypeValue =
      documentType !== null && documentType !== undefined
        ? typeof documentType === "number"
          ? documentType
          : typeof documentType === "object" &&
              documentType?.value !== undefined
            ? documentType.value
            : parseInt(documentType, 10)
        : null;

    // Determine which document fields should have values based on document type and uploaded files
    const hasSelfie = imageFiles && "SELFIE_IMAGE" in imageFiles;
    const hasFrontId = imageFiles && "FRONT_ID_IMAGE" in imageFiles;
    const hasBackId = imageFiles && "BACK_ID_IMAGE" in imageFiles;
    const hasAddress = imageFiles && "ADDRESS_IMAGE" in imageFiles;
    // Prepare USER_DOCUMENTS structure (matches Flutter exactly from lines 4441-4452)
    // Flutter structure:
    // 'SELFIE_IMAGE': UserDocumentSubType.SELFIE.value (7)
    // 'FRONT_ID_IMAGE': _userDocumentId!.docSubType?.value (1, 2, 3, 4, or 6)
    // 'BACK_ID_IMAGE': _userDocumentId!.docSubType?.value ?? '' (Same as front, or empty string if PASSPORT/VISA)
    // 'ADDRESS_IMAGE': _userDocumentAddress!.docSubType?.value (8, 9, 10, or 12)
    const userDocuments = {
      SELFIE_IMAGE: hasSelfie ? UserDocumentSubType.SELFIE : null, // Always 7 if present
      FRONT_ID_IMAGE:
        hasFrontId && documentTypeValue !== null
          ? documentTypeValue // Identity document type: 1, 2, 3, 4, 5, or 6
          : null,
      BACK_ID_IMAGE:
        hasBackId && documentTypeValue !== null
          ? documentTypeValue // Same as FRONT_ID_IMAGE (or empty string if PASSPORT/VISA - but we send it if image exists)
          : "", // Flutter uses empty string if null
      ADDRESS_IMAGE:
        hasAddress && documentTypeValue !== null
          ? documentTypeValue // Address document type: 8, 9, 10, or 12
          : null,
      SHAREHOLDER_DOCUMENT_TYPE: null, // Not used in individual account creation
      DIRECTOR_DOCUMENT_TYPE: null, // Not used in individual account creation
      BENIFICAL_DOCUMENT_TYPE: UserDocumentSubType.ID_CARD, // Always 1 (matches Flutter)
      SIGNATURE_DOCUMENT_TYPE: UserDocumentSubType.SIGNATURE, // Always 13 (matches Flutter)
      SIGNATURE_VIDEO_TYPE: UserDocumentSubType.SIGNATURE_VIDEO, // Always 16 (matches Flutter)
      SIGNATURE_CHECK_TYPE: UserDocumentSubType.CHECK_SIGNATURE, // Always 15 (matches Flutter)
    };
    // Prepare data structure matching the provided request format
    const data = {
      EMAIL: email,
      IS_CREATE: 1,
      // Include USER_ID if available (for existing user profile updates)
      // If not provided, server will extract from JWT token (matches Flutter behavior)
      ...(userId !== null && userId !== undefined ? { USER_ID: userId } : {}),
      USER_INFORMATION: {
        EMAIL: email,
        // These fields may not be available - set to null if not found
        REGISTER_NUMBER: null, // Not collected in current flow
        REGISTER_PLACE: null, // Not collected in current flow
        OTHER_NATIONALITY: urbanInfoData?.OTHER_NATIONALITY || null,
        USER_OCCUPATION: null, // Not collected in current flow - could be added later
        IDENTIFICATION_NUMBER: identificationNumber
          ? parseInt(identificationNumber, 10)
          : null,
      },
      USER_DOCUMENTS: userDocuments,
      // Add agreement IDs dynamically
      ...agreementIds,
    };
    // Encrypt the data
    const dataToEncrypt = {
      HEADERS: extraInfo,
      BODY: data,
    };

    const encryptedResult = await encryptData(dataToEncrypt);
    const messagesArray = Array.isArray(encryptedResult)
      ? encryptedResult
      : [encryptedResult];

    const fullUrl = baseURL
      ? `${baseURL}/v1/user/create-user-documents-mt5-account`
      : API_ROUTES.CREATE_USER_DOCUMENTS_MT5_ACCOUNT;

    // Create FormData for multipart/form-data request
    const formData = new FormData();

    // Add encrypted MESSAGES as JSON string
    formData.append("MESSAGES", JSON.stringify(messagesArray));
    formData.append("PUBLIC_KEY", publicKey);
    formData.append("TYPE", "0");
    formData.append("VERSION_CODE", "0.5.9");

    // Add image files
    for (const [key, file] of Object.entries(imageFiles)) {
      if (file) {
        formData.append(key, file, file.name || "image.jpg");
      } else {
      }
    }

    // Log FormData contents (for debugging)

    const headers = {
      // Don't set Content-Type - browser will set it with boundary for FormData
    };

    let response;
    try {
      response = await axios.post(fullUrl, formData, { headers });
    } catch (axiosError) {
      if (axiosError.response) {
        response = axiosError.response;
      } else {
        throw axiosError;
      }
    }

    // Decrypt response (same logic as createUserDocumentsMT5Account)
    const decryptedResponse = await resolveResponseDecryptedData(response);
    return {
      ...response,
      decryptedData: decryptedResponse || response.data,
    };
  } catch (error) {
    throw error;
  }
};

const updateEmail = async (email, options = {}) => {
  const { baseURL = "" } = options;

  try {
    // Get authentication token from storage
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token || token === "FIRST_TIME_LOGIN") {
      throw new Error("Authentication token is required. Please login first.");
    }

    // Build extra info
    const extraInfo = await buildExtraInfo();

    // Build data to encrypt
    const dataToEncrypt = {
      HEADERS: extraInfo,
      BODY: {
        EMAIL: email,
      },
    };

    // Encrypt the data
    const encryptedMessages = await encryptData(dataToEncrypt);

    // Build request body
    const requestBody = {
      MESSAGES: Array.isArray(encryptedMessages)
        ? encryptedMessages
        : [encryptedMessages],
    };

    const fullUrl = baseURL
      ? `${baseURL}/v1/user/update-email`
      : API_ROUTES.UPDATE_EMAIL;

    // Include Authorization header with Bearer token
    const headers = {
      "Content-Type": "application/json; charset=UTF-8",
      Authorization: `Bearer ${token}`,
    };

    let response;
    try {
      response = await axios.post(fullUrl, requestBody, { headers });
    } catch (axiosError) {
      if (axiosError.response) {
        response = axiosError.response;
      } else {
        throw axiosError;
      }
    }

    // Decrypt response
    const decryptedResponse = await resolveResponseDecryptedData(response);
    return {
      ...response,
      decryptedData: decryptedResponse || response.data,
    };
  } catch (error) {
    throw error;
  }
};

const validateOpt = async (verificationCode, options = {}) => {
  const { baseURL = "" } = options;

  try {
    // Get authentication token from storage
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token || token === "FIRST_TIME_LOGIN") {
      throw new Error("Authentication token is required. Please login first.");
    }

    // Build extra info
    const extraInfo = await buildExtraInfo();

    // Build data to encrypt
    const dataToEncrypt = {
      HEADERS: extraInfo,
      BODY: {
        TYPE: "2",
        VERIFICATION_CODE: verificationCode,
      },
    };

    // Encrypt the data
    const encryptedMessages = await encryptData(dataToEncrypt);

    // Build request body
    const requestBody = {
      MESSAGES: Array.isArray(encryptedMessages)
        ? encryptedMessages
        : [encryptedMessages],
    };

    const fullUrl = baseURL
      ? `${baseURL}/v1/user/validate-opt`
      : API_ROUTES.VALIDATE_OPT;

    // Include Authorization header with Bearer token
    const headers = {
      "Content-Type": "application/json; charset=UTF-8",
      Authorization: `Bearer ${token}`,
    };

    let response;
    try {
      response = await axios.post(fullUrl, requestBody, { headers });
    } catch (axiosError) {
      if (axiosError.response) {
        response = axiosError.response;
      } else {
        throw axiosError;
      }
    }

    // Decrypt response
    const decryptedResponse = await resolveResponseDecryptedData(response);
    return {
      ...response,
      decryptedData: decryptedResponse || response.data,
    };
  } catch (error) {
    throw error;
  }
};

const getTradingInfos = async (options = {}) => {
  const { baseURL = "" } = options;

  try {
    // Get authentication token from storage
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token || token === "FIRST_TIME_LOGIN") {
      throw new Error("Authentication token is required. Please login first.");
    }

    // Build extra info
    const extraInfo = await buildExtraInfo();
    // Build data to encrypt
    const dataToEncrypt = {
      HEADERS: extraInfo,
      BODY: {}, // Empty body for get-trading-infos
    };
    // Encrypt the data
    const encryptedMessages = await encryptData(dataToEncrypt);
    // Build request body
    const requestBody = {
      MESSAGES: Array.isArray(encryptedMessages)
        ? encryptedMessages
        : [encryptedMessages],
    };

    const fullUrl = baseURL
      ? `${baseURL}/v1/user/get-trading-infos`
      : API_ROUTES.GET_TRADING_INFOS;
    // Include Authorization header with Bearer token
    const headers = {
      "Content-Type": "application/json; charset=UTF-8",
      Authorization: `Bearer ${token}`,
    };

    let response;
    try {
      response = await axios.post(fullUrl, requestBody, { headers });
    } catch (axiosError) {
      if (axiosError.response) {
        response = axiosError.response;
      } else {
        throw axiosError;
      }
    }

    const decryptedResponse = await resolveResponseDecryptedData(response);
    return {
      ...response,
      decryptedData: decryptedResponse || response.data,
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Update default (user-level) trading informations.
 * This mirrors the mobile TradingInformationScreen behavior:
 * - Updates DEFAULT_LEVERAGE and REFERAL_BROKER on the user profile (no ID field).
 */
const updateTradingInfos = async (referalBroker, leverage, options = {}) => {
  const { baseURL = "" } = options;

  try {
    // Get authentication token from storage
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token || token === "FIRST_TIME_LOGIN") {
      throw new Error("Authentication token is required. Please login first.");
    }

    // Build extra info
    const extraInfo = await buildExtraInfo();

    // Build data to encrypt with exact field names as specified
    // REFERAL_BROKER can be null if not provided
    const body = {
      LEVERAGE: leverage.toString().trim(),
    };

    if (
      referalBroker !== null &&
      referalBroker !== undefined &&
      referalBroker.toString().trim() !== ""
    ) {
      body.REFERAL_BROKER = referalBroker.toString().trim();
    } else {
      body.REFERAL_BROKER = null;
    }

    const dataToEncrypt = {
      HEADERS: extraInfo,
      BODY: body,
    };
    // Encrypt the data
    const encryptedMessages = await encryptData(dataToEncrypt);
    // Build request body
    const requestBody = {
      MESSAGES: Array.isArray(encryptedMessages)
        ? encryptedMessages
        : [encryptedMessages],
    };

    const fullUrl = baseURL
      ? `${baseURL}/v1/user/update-trading-infos`
      : API_ROUTES.UPDATE_TRADING_INFOS;

    // Include Authorization header with Bearer token
    const headers = {
      "Content-Type": "application/json; charset=UTF-8",
      Authorization: `Bearer ${token}`,
    };
    let response;
    try {
      response = await axios.post(fullUrl, requestBody, { headers });
    } catch (axiosError) {
      if (axiosError.response) {
        response = axiosError.response;
      } else {
        throw axiosError;
      }
    }

    // Decrypt response
    const decryptedResponse = await resolveResponseDecryptedData(response);
    return {
      ...response,
      decryptedData: decryptedResponse || response.data,
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Update a specific trading account's informations.
 * Mirrors the mobile AccountInformationScreen._updateInfromations payload:
 *  - ID (wallet/account identifier, required)
 *  - LEVERAGE (required)
 *  - REFERAL_BROKER (nullable)
 *  - TRADING_GROUP (nullable)
 *  - ACCOUNT_ENABLE (0/1, required for status update)
 *  - TRADING_ENABLE (0/1, required for status update)
 *  - NAME (nullable)
 * The body object passed in should already contain these keys when relevant.
 */
const updateTradingAccountInfos = async (body, options = {}) => {
  const { baseURL = "" } = options;
  if (!body || typeof body !== "object") {
    throw new Error(
      "A valid body object is required for updating trading account information.",
    );
  }
  return postEncryptedJson(API_ROUTES.UPDATE_TRADING_INFOS, body, { baseURL });
};

/**
 * Set trading nickname (mobile DialogNameWidget equivalent).
 * BODY: { ID, NAME }
 */
const setTradingAccountName = async (id, name, options = {}) => {
  const { baseURL = "" } = options;
  return postEncryptedJson(
    API_ROUTES.SET_ACCOUNT_NAME,
    { ID: id, NAME: name },
    { baseURL },
  );
};

/**
 * Reset MT5 password (main or investor) for a specific trading account.
 * PASSWORD_TYPE:
 *  - 0 => Main password
 *  - 1 => Investor password
 */
const resetMt5Password = async (accountId, passwordType = 0, options = {}) => {
  const { baseURL = "" } = options;

  try {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token || token === "FIRST_TIME_LOGIN") {
      throw new Error("Authentication token is required. Please login first.");
    }

    if (!accountId) {
      throw new Error("ACCOUNT_ID is required to reset MT5 password.");
    }

    const extraInfo = await buildExtraInfo();

    const body = {
      ACCOUNT_ID: accountId,
      PASSWORD_TYPE: passwordType,
    };

    const dataToEncrypt = {
      HEADERS: extraInfo,
      BODY: body,
    };

    const encryptedMessages = await encryptData(dataToEncrypt);

    const requestBody = {
      MESSAGES: Array.isArray(encryptedMessages)
        ? encryptedMessages
        : [encryptedMessages],
    };

    const fullUrl = baseURL
      ? `${baseURL}/v1/user/reset-mt5-password`
      : API_ROUTES.RESET_MT5_PASSWORD;

    const headers = {
      "Content-Type": "application/json; charset=UTF-8",
      Authorization: `Bearer ${token}`,
    };

    let response;
    try {
      response = await axios.post(fullUrl, requestBody, { headers });
    } catch (axiosError) {
      if (axiosError.response) {
        response = axiosError.response;
      } else {
        throw axiosError;
      }
    }

    const decryptedResponse = await resolveResponseDecryptedData(response);
    return {
      ...response,
      decryptedData: decryptedResponse || response.data,
    };
  } catch (error) {
    throw error;
  }
};

const getFinancialInfos = async (options = {}) => {
  const { baseURL = "" } = options;

  try {
    // Get authentication token from storage
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token || token === "FIRST_TIME_LOGIN") {
      throw new Error("Authentication token is required. Please login first.");
    }

    // Build extra info
    const extraInfo = await buildExtraInfo();
    // Build data to encrypt
    const dataToEncrypt = {
      HEADERS: extraInfo,
      BODY: {}, // Empty body for get-financial-infos
    };
    // Encrypt the data
    const encryptedMessages = await encryptData(dataToEncrypt);
    // Build request body
    const requestBody = {
      MESSAGES: Array.isArray(encryptedMessages)
        ? encryptedMessages
        : [encryptedMessages],
    };

    const fullUrl = baseURL
      ? `${baseURL}/v1/user/get-financial-infos`
      : API_ROUTES.GET_FINANCIAL_INFOS;
    // Include Authorization header with Bearer token
    const headers = {
      "Content-Type": "application/json; charset=UTF-8",
      Authorization: `Bearer ${token}`,
    };
    let response;
    try {
      response = await axios.post(fullUrl, requestBody, { headers });
    } catch (axiosError) {
      if (axiosError.response) {
        response = axiosError.response;
      } else {
        throw axiosError;
      }
    }

    const decryptedResponse = await resolveResponseDecryptedData(response);
    return {
      ...response,
      decryptedData: decryptedResponse || response.data,
    };
  } catch (error) {
    throw error;
  }
};

const updateFinancialInfos = async (financialData, options = {}) => {
  const { baseURL = "" } = options;

  try {
    // Get authentication token from storage
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token || token === "FIRST_TIME_LOGIN") {
      throw new Error("Authentication token is required. Please login first.");
    }

    // Build extra info
    const extraInfo = await buildExtraInfo();
    // Get USER_ID from storage
    const userId =
      localStorage.getItem("userId") || sessionStorage.getItem("userId");

    // Build BODY with USER_ID and financial data
    // financialData should be either { bank: {...} } or { binance: {...} }
    const bodyData = {
      USER_ID: userId ? parseInt(userId, 10) : null,
      ...financialData,
    };

    // If financialData has 'crypto' key, rename it to 'binance'
    if (bodyData.crypto) {
      bodyData.binance = bodyData.crypto;
      delete bodyData.crypto;
    }

    // Build data to encrypt - structure: { HEADERS: extraInfo, BODY: { USER_ID, bank/binance } }
    const dataToEncrypt = {
      HEADERS: extraInfo,
      BODY: bodyData,
    };

    // Encrypt the data
    const encryptedMessages = await encryptData(dataToEncrypt);
    // Build request body
    const requestBody = {
      MESSAGES: Array.isArray(encryptedMessages)
        ? encryptedMessages
        : [encryptedMessages],
    };

    const fullUrl = baseURL
      ? `${baseURL}/v1/user/update-financial-infos`
      : API_ROUTES.UPDATE_FINANCIAL_INFOS;

    // Include Authorization header with Bearer token
    const headers = {
      "Content-Type": "application/json; charset=UTF-8",
      Authorization: `Bearer ${token}`,
    };
    let response;
    try {
      response = await axios.post(fullUrl, requestBody, { headers });
    } catch (axiosError) {
      if (axiosError.response) {
        response = axiosError.response;
      } else {
        throw axiosError;
      }
    }

    // Decrypt response
    const decryptedResponse = await resolveResponseDecryptedData(response);
    return {
      ...response,
      decryptedData: decryptedResponse || response.data,
    };
  } catch (error) {
    throw error;
  }
};

const deleteFinancialInfos = async (type, options = {}) => {
  const { baseURL = "" } = options;

  try {
    // Get authentication token from storage
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token || token === "FIRST_TIME_LOGIN") {
      throw new Error("Authentication token is required. Please login first.");
    }

    // Build extra info
    const extraInfo = await buildExtraInfo();

    // Build data to encrypt - TYPE should be "bank" or "binance" (for crypto)
    const dataToEncrypt = {
      HEADERS: extraInfo,
      BODY: {
        TYPE: type, // "bank" or "binance"
      },
    };

    // Encrypt the data
    const encryptedMessages = await encryptData(dataToEncrypt);
    // Build request body
    const requestBody = {
      MESSAGES: Array.isArray(encryptedMessages)
        ? encryptedMessages
        : [encryptedMessages],
    };

    const fullUrl = baseURL
      ? `${baseURL}/v1/user/delete-financial-infos`
      : API_ROUTES.DELETE_FINANCIAL_INFOS;

    // Include Authorization header with Bearer token
    const headers = {
      "Content-Type": "application/json; charset=UTF-8",
      Authorization: `Bearer ${token}`,
    };
    let response;
    try {
      response = await axios.post(fullUrl, requestBody, { headers });
    } catch (axiosError) {
      if (axiosError.response) {
        response = axiosError.response;
      } else {
        throw axiosError;
      }
    }

    // Decrypt response
    const decryptedResponse = await resolveResponseDecryptedData(response);
    return {
      ...response,
      decryptedData: decryptedResponse || response.data,
    };
  } catch (error) {
    throw error;
  }
};

const getCashInMethod = async (language = "en", options = {}) => {
  const { baseURL = "" } = options;

  try {
    // Get authentication token from storage
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token || token === "FIRST_TIME_LOGIN") {
      throw new Error("Authentication token is required. Please login first.");
    }

    // Build extra info
    const extraInfo = await buildExtraInfo();
    // Build data to encrypt
    const dataToEncrypt = {
      HEADERS: extraInfo,
      BODY: {
        LANGUAGE: language,
      },
    };

    // Encrypt the data
    const encryptedMessages = await encryptData(dataToEncrypt);
    // Build request body
    const requestBody = {
      MESSAGES: Array.isArray(encryptedMessages)
        ? encryptedMessages
        : [encryptedMessages],
    };

    const fullUrl = baseURL
      ? `${baseURL}/v1/user/get-cashin-method`
      : API_ROUTES.GET_CASHIN_METHOD;
    // Include Authorization header with Bearer token
    const headers = {
      "Content-Type": "application/json; charset=UTF-8",
      Authorization: `Bearer ${token}`,
    };
    let response;
    try {
      response = await axios.post(fullUrl, requestBody, { headers });
    } catch (axiosError) {
      if (axiosError.response) {
        response = axiosError.response;
      } else {
        throw axiosError;
      }
    }

    const decryptedResponse = await resolveResponseDecryptedData(response);
    return {
      ...response,
      decryptedData: decryptedResponse || response.data,
    };
  } catch (error) {
    throw error;
  }
};

const getTradingAccountsTrx = async (
  type = [0, 1],
  withUsd = 0,
  options = {},
) => {
  if (!getTradingAccountsTrx._cache) getTradingAccountsTrx._cache = new Map();
  if (!getTradingAccountsTrx._inFlight)
    getTradingAccountsTrx._inFlight = new Map();

  const cacheKey = JSON.stringify({
    type: Array.isArray(type) ? type : [type],
    withUsd,
    baseURL: options?.baseURL || "",
  });
  const cacheTtlMs = 30 * 1000;
  const cached = getTradingAccountsTrx._cache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < cacheTtlMs) {
    return cached.value;
  }
  const inFlight = getTradingAccountsTrx._inFlight.get(cacheKey);
  if (inFlight) return await inFlight;

  const { baseURL = "" } = options;

  try {
    // Get authentication token from storage
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token || token === "FIRST_TIME_LOGIN") {
      throw new Error("Authentication token is required. Please login first.");
    }

    // Build extra info
    const extraInfo = await buildExtraInfo();
    // Build data to encrypt
    const dataToEncrypt = {
      HEADERS: extraInfo,
      BODY: {
        TYPE: type,
        WITH_USD: withUsd,
      },
    };

    // Encrypt the data
    const encryptedMessages = await encryptData(dataToEncrypt);
    // Build request body
    const requestBody = {
      MESSAGES: Array.isArray(encryptedMessages)
        ? encryptedMessages
        : [encryptedMessages],
    };

    const fullUrl = baseURL
      ? `${baseURL}/v1/user/get-trading-accounts-trx`
      : API_ROUTES.GET_TRADING_ACCOUNTS_TRX;
    // Include Authorization header with Bearer token
    const headers = {
      "Content-Type": "application/json; charset=UTF-8",
      Authorization: `Bearer ${token}`,
    };
    const requestPromise = (async () => {
      let response;
      try {
        response = await axios.post(fullUrl, requestBody, { headers });
      } catch (axiosError) {
        if (axiosError.response) {
          response = axiosError.response;
        } else {
          throw axiosError;
        }
      }

      const decryptedResponse = await resolveResponseDecryptedData(response);
      const finalResponse = {
        ...response,
        decryptedData: decryptedResponse || response.data,
      };

      const code = Number(finalResponse?.decryptedData?.CODE);
      if (code === AppConstants.responseOk) {
        getTradingAccountsTrx._cache.set(cacheKey, {
          timestamp: Date.now(),
          value: finalResponse,
        });
      }

      return finalResponse;
    })();

    getTradingAccountsTrx._inFlight.set(cacheKey, requestPromise);
    try {
      return await requestPromise;
    } finally {
      getTradingAccountsTrx._inFlight.delete(cacheKey);
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Helper: POST encrypted JSON (Bearer token). Returns { ...response, decryptedData }.
 */
const postEncryptedJson = async (url, body, options = {}) => {
  const { baseURL = "" } = options;
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");
  if (!token || token === "FIRST_TIME_LOGIN") {
    throw new Error("Authentication token is required. Please login first.");
  }
  const extraInfo = await buildExtraInfo();
  const dataToEncrypt = { HEADERS: extraInfo, BODY: body };
  const encryptedMessages = await encryptData(dataToEncrypt);
  const messagesArray = Array.isArray(encryptedMessages)
    ? encryptedMessages
    : [encryptedMessages];

  const requestBody = { MESSAGES: messagesArray };
  if (needsPublicKey(url)) {
    const publicKey = await getUserPublicKey();
    requestBody.PUBLIC_KEY = publicKey;
    requestBody.TYPE = "0";
  }

  const fullUrl = baseURL
    ? `${baseURL.replace(/\/$/, "")}/v1/user/${url.split("/").pop()}`
    : url;
  const headers = {
    "Content-Type": "application/json; charset=UTF-8",
    Authorization: `Bearer ${token}`,
    "device-id": extraInfo["device-id"] ?? "",
    "device-model": extraInfo["device-model"] ?? "",
    "device-os": extraInfo["device-os"] ?? "",
    "device-ip": extraInfo["device-ip"] ?? "null",
    "device-position":
      extraInfo["device-position"] ??
      JSON.stringify({ LATITUDE: 0, LONGTITUDE: 0 }),
    "user-type": String(extraInfo["user-type"] ?? "0"),
  };
  let response;
  try {
    response = await axios.post(fullUrl, requestBody, { headers });
  } catch (e) {
    if (e.response) {
      response = e.response;
    } else {
      throw e;
    }
  }

  // Handle critical backend errors that should force logout (e.g. SecretKey middleware failures)
  try {
    if (response && typeof response.data === "string") {
      const raw = response.data;
      if (
        raw.includes("App\\Http\\Middleware\\Client\\SecretKey") ||
        raw.includes("SecretKey::isValidSecretKey")
      ) {
        // Force logout and redirect to splash screen
        handleNetworkErrorLogout();
        const err = new Error(
          "Session error detected. You have been logged out, please login again.",
        );
        err.isCriticalBackendError = true;
        throw err;
      }
    }
  } catch (criticalError) {
    // Rethrow to caller – decryptedResponse is not reliable in this case
    throw criticalError;
  }
  let decryptedResponse = null;
  try {
    decryptedResponse = await decryptResponseMessages(response.data);
    if (!decryptedResponse && response.data) {
      decryptedResponse = response.data;
    }
  } catch (_) {
    decryptedResponse = response.data || {};
  }

  // Secondary check in case the critical error message is inside decrypted payload
  try {
    const messageSource =
      typeof decryptedResponse === "string"
        ? decryptedResponse
        : decryptedResponse?.MESSAGE || decryptedResponse?.message || null;
    if (
      typeof messageSource === "string" &&
      (messageSource.includes("App\\Http\\Middleware\\Client\\SecretKey") ||
        messageSource.includes("SecretKey::isValidSecretKey"))
    ) {
      handleNetworkErrorLogout();
      const err = new Error(
        "Session error detected. You have been logged out, please login again.",
      );
      err.isCriticalBackendError = true;
      throw err;
    }
  } catch (criticalError) {
    throw criticalError;
  }

  return { ...response, decryptedData: decryptedResponse || response.data };
};

/**
 * POST encrypted FormData with bearer token auth — for file uploads.
 * Mirrors Flutter's DatabaseApi.callServer with imageFile parameter.
 */
const postEncryptedFormData = async (
  url,
  body,
  imageFiles = {},
  options = {},
) => {
  const { baseURL = "" } = options;
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");
  if (!token || token === "FIRST_TIME_LOGIN") {
    throw new Error("Authentication token is required. Please login first.");
  }
  const extraInfo = await buildExtraInfo();
  const dataToEncrypt = { HEADERS: extraInfo, BODY: body };
  const encryptedMessages = await encryptData(dataToEncrypt);
  const messagesArray = Array.isArray(encryptedMessages)
    ? encryptedMessages
    : [encryptedMessages];
  const fullUrl = baseURL
    ? `${baseURL.replace(/\/$/, "")}/v1/user/${url.split("/").pop()}`
    : url;

  const formData = new FormData();
  formData.append("MESSAGES", JSON.stringify(messagesArray));
  if (needsPublicKey(url)) {
    const publicKey = await getUserPublicKey();
    formData.append("PUBLIC_KEY", publicKey);
    formData.append("TYPE", "0");
  }

  for (const [key, file] of Object.entries(imageFiles)) {
    if (file) {
      formData.append(key, file, file.name || `${key}.jpg`);
    }
  }

  const headers = {
    Authorization: `Bearer ${token}`,
    "device-id": extraInfo["device-id"] ?? "",
    "device-model": extraInfo["device-model"] ?? "",
    "device-os": extraInfo["device-os"] ?? "",
    "device-ip": extraInfo["device-ip"] ?? "null",
    "device-position":
      extraInfo["device-position"] ??
      JSON.stringify({ LATITUDE: 0, LONGTITUDE: 0 }),
    "user-type": String(extraInfo["user-type"] ?? "0"),
  };

  let response;
  try {
    response = await axios.post(fullUrl, formData, { headers });
  } catch (e) {
    if (e.response) response = e.response;
    else throw e;
  }

  let decryptedResponse = null;
  try {
    decryptedResponse = await decryptResponseMessages(response.data);
    if (!decryptedResponse && response.data) {
      decryptedResponse = response.data;
    }
  } catch (_) {
    decryptedResponse = response.data || {};
  }
  return { ...response, decryptedData: decryptedResponse || response.data };
};

/**
 * Create a new trading account.
 * BODY: { ACCOUNT_TYPE }
 * Uses encrypted MESSAGES with extra headers and includes Authorization token.
 */
const createNewTradingAccount = async (accountType = 0, options = {}) => {
  const body = {
    ACCOUNT_TYPE: accountType,
  };
  return postEncryptedJson(
    API_ROUTES.CREATE_NEW_TRADING_ACCOUNT,
    body,
    options,
  );
};

/**
 * CTS: Check if user must sign CTS for given amount/currency.
 * @param {{ amount?: number, currencyId?: number }} params
 * @returns {Promise<{ mustSign: boolean, ctsData: object | null, error: boolean }>}
 */
const checkCtsMustSign = async (params = {}, options = {}) => {
  const body = {};
  if (params.amount != null) body.AMOUNT = params.amount;
  if (params.currencyId != null) body.CURRENCY_ID = params.currencyId;
  try {
    const res = await postEncryptedJson(
      API_ROUTES.CHECK_CTS_MUST_SIGN,
      body,
      options,
    );
    const d = res.decryptedData || {};
    const code = Number(d.CODE);
    if (code === AppConstants.responseOk || code === 200) {
      const mustSign = d.MUST_SIGN === true || d.MUST_SIGN === 1;
      let ctsData = null;
      if (d.CTS_ID != null) {
        ctsData = {
          ctsId: d.CTS_ID,
          status: d.STATUS,
          hasSignature: d.HAS_SIGNATURE === true || d.HAS_SIGNATURE === 1,
        };
      }
      return { mustSign, ctsData, error: false };
    }
    if (code === 206) return { mustSign: false, ctsData: null, error: false };
    if (!Number.isNaN(code)) {
    }
    return { mustSign: false, ctsData: null, error: true };
  } catch (e) {
    return { mustSign: false, ctsData: null, error: true };
  }
};

/**
 * CTS: Get today's CTS data (source of funds, signature, etc.).
 * @returns {Promise<{ ctsId?: number, sourceOfFunds?: number, isSigned?: boolean, hasSignature?: boolean, status?: number, signatureImageName?: string, signatureImageUrl?: string } | null>}
 */
/**
 * CTS: Get today's CTS data.
 * Matches Flutter CtsDailyModel.fromJson() structure.
 * @returns {Promise<{ ctsId?: number, ctsDate?: string, sourceOfFunds?: number, totalAmount?: number, status?: number, hasAttachment?: boolean, hasSignature?: boolean, attachmentId?: number, signatureId?: number, signatureUrl?: string } | null>}
 */
const viewCtsToday = async (options = {}) => {
  try {
    const res = await postEncryptedJson(API_ROUTES.VIEW_CTS_TODAY, {}, options);
    const d = res.decryptedData || {};

    // When CODE == 200 we have a full CTS record for today.
    // When CODE == 206 we might still get default values like SOURCE_OF_FUNDS
    // even if the CTS was not formally created yet.
    if (d.CODE !== AppConstants.responseOk && d.CODE !== 206) return null;

    const hasAttachment = d.HAS_ATTACHMENT === true || d.HAS_ATTACHMENT === 1;
    const hasSignature = d.HAS_SIGNATURE === true || d.HAS_SIGNATURE === 1;

    // Get signature image name/URL from response
    const signatureImageName =
      d.SIGNATURE_IMAGE_NAME ||
      d.SIGNATURE_NAME ||
      d.SIGNATURE ||
      d.SIGNATURE_IMAGE ||
      null;
    let signatureUrl = null;
    if (signatureImageName) {
      // Construct image URL using the API base URL pattern
      signatureUrl = `${APIURI}v1/user/images/${signatureImageName}?type=13`; // 13 is SIGNATURE type
    } else if (d.SIGNATURE_URL) {
      signatureUrl = d.SIGNATURE_URL;
    }

    return {
      ctsId: d.CTS_ID,
      ctsDate: d.CTS_DATE?.toString(),
      sourceOfFunds: d.SOURCE_OF_FUNDS,
      totalAmount: d.TOTAL_AMOUNT != null ? parseFloat(d.TOTAL_AMOUNT) : null,
      status: d.STATUS,
      hasAttachment: hasAttachment,
      hasSignature: hasSignature,
      attachmentId: d.ATTACHMENT_ID,
      signatureId: d.SIGNATURE_ID,
      signatureUrl: signatureUrl,
    };
  } catch (e) {
    return null;
  }
};

/**
 * CTS: Create today's CTS (optional attachment).
 * Matches Flutter's DatabaseApi.callServer logic:
 * - If no files: use JSON (postData)
 * - If files exist: use FormData (postDataAndImage)
 * @param {{ sourceOfFunds: number }} data
 * @param {{ ATTACHMENT?: File }} files
 */
const createCtsToday = async (data, files = {}, options = {}) => {
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");
  if (!token || token === "FIRST_TIME_LOGIN")
    throw new Error("Authentication token is required.");
  const extraInfo = await buildExtraInfo();
  const body = {
    SOURCE_OF_FUNDS: data.sourceOfFunds,
    TOTAL_AMOUNT: data.totalAmount != null ? data.totalAmount : 0,
  };
  const dataToEncrypt = { HEADERS: extraInfo, BODY: body };
  const encryptedMessages = await encryptData(dataToEncrypt);
  const fullUrl = options.baseURL
    ? `${options.baseURL}/v1/user/create-cts-today`
    : API_ROUTES.CREATE_CTS_TODAY;

  // Check if there are any files (matches Flutter line 43: if (imageFile.isEmpty))
  const hasFiles = files.ATTACHMENT != null;

  let response;
  const headers = {
    Authorization: `Bearer ${token}`,
    "device-id": extraInfo["device-id"] ?? "",
    "device-model": extraInfo["device-model"] ?? "",
    "device-os": extraInfo["device-os"] ?? "",
    "device-ip": extraInfo["device-ip"] ?? "null",
    "device-position":
      extraInfo["device-position"] ??
      JSON.stringify({ LATITUDE: 0, LONGTITUDE: 0 }),
    "user-type": String(extraInfo["user-type"] ?? "0"),
  };

  try {
    if (hasFiles) {
      // Use FormData when files are present (matches Flutter postDataAndImage)
      const formData = new FormData();
      formData.append(
        "MESSAGES",
        JSON.stringify(
          Array.isArray(encryptedMessages)
            ? encryptedMessages
            : [encryptedMessages],
        ),
      );
      if (files.ATTACHMENT) {
        formData.append(
          "ATTACHMENT",
          files.ATTACHMENT,
          files.ATTACHMENT.name || "attachment",
        );
      }
      response = await axios.post(fullUrl, formData, { headers });
    } else {
      // Use JSON when no files (matches Flutter postData)
      const jsonBody = {
        MESSAGES: Array.isArray(encryptedMessages)
          ? encryptedMessages
          : [encryptedMessages],
      };
      headers["Content-Type"] = "application/json; charset=UTF-8";
      response = await axios.post(fullUrl, jsonBody, { headers });
    }
  } catch (e) {
    if (e.response) response = e.response;
    else throw e;
  }

  let decryptedResponse = null;
  try {
    decryptedResponse = await decryptResponseMessages(response.data);
    if (!decryptedResponse && response.data) decryptedResponse = response.data;
  } catch (_) {
    decryptedResponse = response.data || {};
  }
  return { ...response, decryptedData: decryptedResponse || response.data };
};

/**
 * CTS: Sign today's CTS with signature image.
 * Uses CREATE_CTS_TODAY endpoint as per user requirement (instead of SIGN_CTS).
 * Matches Flutter's _signCts method structure but uses CREATE_CTS_TODAY endpoint.
 * @param {File} signatureFile
 * @param {number} [sourceOfFunds] - Optional source of funds (may be required by backend)
 */
const signCts = async (signatureFile, sourceOfFunds = null, options = {}) => {
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");
  if (!token || token === "FIRST_TIME_LOGIN")
    throw new Error("Authentication token is required.");
  const extraInfo = await buildExtraInfo();

  // When using CREATE_CTS_TODAY for signing, we may need to send source of funds
  const body = {};
  if (sourceOfFunds != null) {
    body.SOURCE_OF_FUNDS = sourceOfFunds;
    body.TOTAL_AMOUNT = 0;
  }

  const dataToEncrypt = { HEADERS: extraInfo, BODY: body };
  const encryptedMessages = await encryptData(dataToEncrypt);

  // Matches Flutter: use FormData when files are present (postDataAndImage)
  const formData = new FormData();
  formData.append(
    "MESSAGES",
    JSON.stringify(
      Array.isArray(encryptedMessages)
        ? encryptedMessages
        : [encryptedMessages],
    ),
  );

  // Ensure signature file has proper name and type
  const signatureName = signatureFile.name || "signature.png";
  formData.append("SIGNATURE", signatureFile, signatureName);

  // Use CREATE_CTS_TODAY endpoint (as per user requirement, instead of SIGN_CTS)
  const fullUrl = options.baseURL
    ? `${options.baseURL}/v1/user/sign-cts`
    : API_ROUTES.SIGN_CTS;
  const headers = {
    Authorization: `Bearer ${token}`,
    "device-id": extraInfo["device-id"] ?? "",
    "device-model": extraInfo["device-model"] ?? "",
    "device-os": extraInfo["device-os"] ?? "",
    "device-ip": extraInfo["device-ip"] ?? "null",
    "device-position":
      extraInfo["device-position"] ??
      JSON.stringify({ LATITUDE: 0, LONGTITUDE: 0 }),
    "user-type": String(extraInfo["user-type"] ?? "0"),
  };
  let response;
  try {
    response = await axios.post(fullUrl, formData, { headers });
  } catch (e) {
    if (e.response) response = e.response;
    else throw e;
  }
  let decryptedResponse = null;
  try {
    decryptedResponse = await decryptResponseMessages(response.data);
    if (!decryptedResponse && response.data) decryptedResponse = response.data;
  } catch (_) {
    decryptedResponse = response.data || {};
  }
  return { ...response, decryptedData: decryptedResponse || response.data };
};

/**
 * Create cash-in operation. Optional CASHIN_FILE for document.
 * @param {{ type: number, amount: number, walletId?: number, method: number, phoneNumber?: string }} data
 * @param {{ CASHIN_FILE?: File }} files
 */
const createCashInOperation = async (data, files = {}, options = {}) => {
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");
  if (!token || token === "FIRST_TIME_LOGIN")
    throw new Error("Authentication token is required.");
  const extraInfo = await buildExtraInfo();
  const body = {
    TYPE: data.type,
    AMOUNT: data.amount,
    WALLET_ID: data.walletId,
    METHOD: data.method,
    PHONE_NUMBER: data.phoneNumber ?? null,
  };
  const dataToEncrypt = { HEADERS: extraInfo, BODY: body };

  const fullUrl = options.baseURL
    ? `${options.baseURL}/v1/user/create-cash-in-operation`
    : API_ROUTES.CREATE_CASHIN_MONEY_OPERATION;

  const encryptedMessages = await encryptData(dataToEncrypt);
  const formData = new FormData();
  formData.append(
    "MESSAGES",
    JSON.stringify(
      Array.isArray(encryptedMessages)
        ? encryptedMessages
        : [encryptedMessages],
    ),
  );
  if (files.CASHIN_FILE)
    formData.append(
      "CASHIN_FILE",
      files.CASHIN_FILE,
      files.CASHIN_FILE.name || "file",
    );

  const headers = {
    Authorization: `Bearer ${token}`,
    "device-id": extraInfo["device-id"] ?? "",
    "device-model": extraInfo["device-model"] ?? "",
    "device-os": extraInfo["device-os"] ?? "",
    "device-ip": extraInfo["device-ip"] ?? "null",
    "device-position":
      extraInfo["device-position"] ??
      JSON.stringify({ LATITUDE: 0, LONGTITUDE: 0 }),
    "user-type": String(extraInfo["user-type"] ?? "0"),
  };
  let response;
  try {
    response = await axios.post(fullUrl, formData, { headers });
  } catch (e) {
    if (e.response) response = e.response;
    else throw e;
  }

  let decryptedResponse = null;
  try {
    decryptedResponse = await decryptResponseMessages(response.data);
    if (!decryptedResponse && response.data) decryptedResponse = response.data;
  } catch (_) {
    decryptedResponse = response.data || {};
  }

  const result = {
    ...response,
    decryptedData: decryptedResponse || response.data,
  };
  return result;
};

/**
 * Create internal transfer money operation.
 * BODY: { AMOUNT, FROM, TO } (matches mobile CREATE_INTERNAL_TRANSFER_MONEY_OPERATION).
 */
const createInternalTransferMoneyOperation = async (data, options = {}) => {
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");
  if (!token || token === "FIRST_TIME_LOGIN") {
    throw new Error("Authentication token is required. Please login first.");
  }

  const extraInfo = await buildExtraInfo();
  const body = {
    AMOUNT: data.amount,
    FROM: data.fromWalletId,
    TO: data.toWalletId,
  };
  const dataToEncrypt = { HEADERS: extraInfo, BODY: body };

  const fullUrl = options.baseURL
    ? `${options.baseURL}/v1/user/create-internal-transfer-operation`
    : API_ROUTES.CREATE_INTERNAL_TRANSFER_MONEY_OPERATION;

  const encryptedMessages = await encryptData(dataToEncrypt);
  const requestBody = {
    MESSAGES: Array.isArray(encryptedMessages)
      ? encryptedMessages
      : [encryptedMessages],
  };

  const headers = {
    "Content-Type": "application/json; charset=UTF-8",
    Authorization: `Bearer ${token}`,
  };

  let response;
  try {
    response = await axios.post(fullUrl, requestBody, { headers });
  } catch (e) {
    if (e.response) response = e.response;
    else throw e;
  }

  const decryptedResponse = await resolveResponseDecryptedData(response);
    return {
      ...response,
      decryptedData: decryptedResponse || response.data };
};

/**
 * Get cash-out payment methods (withdrawal). Uses encrypted POST.
 * @returns {Promise<{ decryptedData: { CODE, DATA } }>}
 */
const getCashOutPaymentMethod = async (options = {}) => {
  const res = await postEncryptedJson(
    API_ROUTES.GET_PAYMENT_METHOD,
    {},
    options,
  );
  return res;
};

/**
 * Create cash-out (withdrawal) operation. No file upload.
 * @param {{ amount: number, walletId: number, method: number, phoneNumber?: string }} data
 */
const createCashOutOperation = async (data, options = {}) => {
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");
  if (!token || token === "FIRST_TIME_LOGIN")
    throw new Error("Authentication token is required.");
  const extraInfo = await buildExtraInfo();
  const body = {
    AMOUNT: data.amount,
    WALLET_ID: data.walletId,
    METHOD: data.method,
    PHONE_NUMBER: data.phoneNumber ?? "",
  };
  const dataToEncrypt = { HEADERS: extraInfo, BODY: body };
  const fullUrl = options.baseURL
    ? `${options.baseURL}/v1/user/create-cash-out-operation`
    : API_ROUTES.CREATE_CASH_OUT_OPERATION;
  const encryptedMessages = await encryptData(dataToEncrypt);
  const formData = new FormData();
  formData.append(
    "MESSAGES",
    JSON.stringify(
      Array.isArray(encryptedMessages)
        ? encryptedMessages
        : [encryptedMessages],
    ),
  );
  const headers = {
    Authorization: `Bearer ${token}`,
    "device-id": extraInfo["device-id"] ?? "",
    "device-model": extraInfo["device-model"] ?? "",
    "device-os": extraInfo["device-os"] ?? "",
    "device-ip": extraInfo["device-ip"] ?? "null",
    "device-position":
      extraInfo["device-position"] ??
      JSON.stringify({ LATITUDE: 0, LONGTITUDE: 0 }),
    "user-type": String(extraInfo["user-type"] ?? "0"),
  };
  let response;
  try {
    response = await axios.post(fullUrl, formData, { headers });
  } catch (e) {
    if (e.response) response = e.response;
    else throw e;
  }
  let decryptedResponse = null;
  try {
    decryptedResponse = await decryptResponseMessages(response.data);
    if (!decryptedResponse && response.data) decryptedResponse = response.data;
  } catch (_) {
    decryptedResponse = response.data || {};
  }
  return { ...response, decryptedData: decryptedResponse || response.data };
};

/**
 * Apply cash-in (password + REQUEST_TOKEN as TRANSACTION_ID). Returns { ...response, decryptedData }.
 */
const applyCashInMoneyOperation = async (
  requestToken,
  plainPassword,
  options = {},
) => {
  const hashed = await hashPassword(plainPassword);
  const body = { TRANSACTION_ID: requestToken, PASSWORD: hashed };
  return postEncryptedJson(
    API_ROUTES.APPLY_CASHIN_MONEY_OPERATION,
    body,
    options,
  );
};

/**
 * Create IB deposit/withdraw operation for a client.
 * BODY: { AMOUNT, WALLET_ID, USER_ID, NOTE, TYPE }
 * Uses the same encrypted MESSAGES + Authorization header strategy as cash-in.
 */
const createDepositWithdrawOperation = async (data, options = {}) => {
  const normalizedNote =
    typeof data.note === "string" ? data.note.trim() : data.note;
  const body = {
    AMOUNT: data.amount,
    WALLET_ID: data.walletId,
    USER_ID: data.userId,
    NOTE: normalizedNote ? normalizedNote : null,
    TYPE: data.type ?? 4,
  };
  return postEncryptedJson(
    API_ROUTES.CREATE_DEPOSIT_WITHDRAW_OPERATION,
    body,
    options,
  );
};

/**
 * Apply IB deposit/withdraw operation (password + TRANSACTION_ID).
 * PASSWORD is hashed before being sent, mirroring cash-in behavior.
 */
const applyDepositWithdrawOperation = async (
  transactionId,
  plainPassword,
  options = {},
) => {
  const hashed = await hashPassword(plainPassword);
  const body = { TRANSACTION_ID: transactionId, PASSWORD: hashed };
  return postEncryptedJson(
    API_ROUTES.APPLY_DEPOSIT_WITHDRAW_OPERATION,
    body,
    options,
  );
};

/**
 * Cancel IB deposit/withdraw operation.
 * BODY: { TRANSACTION_ID }
 */
const cancelDepositWithdrawOperation = async (transactionId, options = {}) => {
  return postEncryptedJson(
    API_ROUTES.CANCEL_DEPOSIT_WITHDRAW_OPERATION,
    { TRANSACTION_ID: transactionId },
    options,
  );
};

/**
 * Cancel demo deposit operation.
 * BODY: { TRANSACTION_ID }
 */
const cancelDepositDemoOperation = async (transactionId, options = {}) => {
  return postEncryptedJson(
    API_ROUTES.CANCEL_DEPOSIT_DEMO_OPERATION,
    { TRANSACTION_ID: transactionId },
    options,
  );
};

/**
 * Send IB notifications.
 * BODY: { TITLE, BODY, RECEIVER, USERS?[] }
 * RECEIVER: 0 => all related users, 4 => custom USERS list.
 */
const sendNotifications = async (
  { title, body, receiver = 0, users = [] } = {},
  options = {},
) => {
  const payload = {
    TITLE: String(title || "").trim(),
    BODY: String(body || "").trim(),
    RECEIVER: Number(receiver),
  };

  if (Number(receiver) === 4) {
    payload.USERS = Array.isArray(users)
      ? users.map((u) => Number(u)).filter((u) => Number.isFinite(u))
      : [];
  }

  return postEncryptedJson(API_ROUTES.SEND_NOTIFICATIONS, payload, options);
};

/**
 * Apply cash-out (password + REQUEST_TOKEN). Returns { ...response, decryptedData }.
 */
const applyCashOutMoneyOperation = async (
  requestToken,
  plainPassword,
  options = {},
) => {
  const hashed = await hashPassword(plainPassword);
  const body = { REQUEST_TOKEN: requestToken, PASSWORD: hashed };
  return postEncryptedJson(API_ROUTES.APPLY_CASH_OUT_OPERATION, body, options);
};

/**
 * Broker: load a cash request for collect flow (cash-in pending or cash-out collected handoff).
 * BODY: { REQUEST_TOKEN }
 */
const getCollectedMoneyRequest = async (requestToken, options = {}) =>
  postEncryptedJson(
    API_ROUTES.GET_COLLECTED_MONEY_REQUEST,
    { REQUEST_TOKEN: String(requestToken || "").trim() },
    options,
  );

/**
 * Broker: load a cash-out request for transfer completion.
 * BODY: { REQUEST_TOKEN }
 */
const getTransferCashOutRequest = async (requestToken, options = {}) =>
  postEncryptedJson(
    API_ROUTES.GET_TRANSFER_CASH_OUT_REQUEST,
    { REQUEST_TOKEN: String(requestToken || "").trim() },
    options,
  );

/** Broker collects cash-in pending request (requestor PASSWORD hashed). */
const applyCollectCashInRequest = async (
  requestToken,
  plainPassword,
  options = {},
) => {
  const hashed = await hashPassword(plainPassword);
  return postEncryptedJson(
    API_ROUTES.APPLY_COLLECT_CASH_IN_REQUEST,
    { REQUEST_TOKEN: String(requestToken || "").trim(), PASSWORD: hashed },
    options,
  );
};

/** Broker completes collected cash-out (requestor PASSWORD hashed). */
const applyCollectCashOutRequest = async (
  requestToken,
  plainPassword,
  options = {},
) => {
  const hashed = await hashPassword(plainPassword);
  return postEncryptedJson(
    API_ROUTES.APPLY_COLLECT_CASH_OUT_REQUEST,
    { REQUEST_TOKEN: String(requestToken || "").trim(), PASSWORD: hashed },
    options,
  );
};

/** Broker applies transfer cash-out (requestor PASSWORD hashed). */
const applyTransferCashOutRequest = async (
  requestToken,
  plainPassword,
  options = {},
) => {
  const hashed = await hashPassword(plainPassword);
  return postEncryptedJson(
    API_ROUTES.APPLY_TRANSFER_CASH_OUT_REQUEST,
    { REQUEST_TOKEN: String(requestToken || "").trim(), PASSWORD: hashed },
    options,
  );
};

/**
 * Create pay-by-card operation. BODY: { WALLET_ID, AMOUNT }.
 * @returns {Promise<{ decryptedData: { CODE, FEES?, TRANSACTION_ID? } }>}
 */
const createPayByCardOperation = async ({ walletId, amount }, options = {}) => {
  const body = {
    WALLET_ID: walletId,
    AMOUNT: amount,
  };
  return postEncryptedJson(
    API_ROUTES.CREATE_PAY_BY_CARD_OPERATION,
    body,
    options,
  );
};

/**
 * Apply pay-by-card (confirm PIN). BODY: { TRANSACTION_ID, PASSWORD } (password hashed).
 */
const applyPayByCardOperation = async (
  transactionId,
  plainPassword,
  options = {},
) => {
  const hashed = await hashPassword(plainPassword);
  const body = { TRANSACTION_ID: transactionId, PASSWORD: hashed };
  return postEncryptedJson(
    API_ROUTES.APPLY_PAY_BY_CARD_OPERATION,
    body,
    options,
  );
};

/**
 * Cancel pay-by-card draft. BODY: { TRANSACTION_ID }.
 */
const cancelPayByCardOperation = async (transactionId, options = {}) => {
  return postEncryptedJson(
    API_ROUTES.CANCEL_PAY_BY_CARD_OPERATION,
    { TRANSACTION_ID: transactionId },
    options,
  );
};

/**
 * Post card gateway result. BODY: { TRANSACTION_ID, TRANSACTION_DATA } (object as returned by gateway client).
 */
const addResultPayByCardOperation = async (
  transactionId,
  transactionData,
  options = {},
) => {
  const body = {
    TRANSACTION_ID: transactionId,
    TRANSACTION_DATA: transactionData,
  };
  return postEncryptedJson(
    API_ROUTES.ADD_RESULT_PAY_BY_CARD_OPERATION,
    body,
    options,
  );
};

/**
 * Opens PayTabs Hosted Payment Page: encrypted POST, returns REDIRECT_URL in decrypted payload.
 * @param {string|number} transactionId
 */
const createPayByCardHostedPayment = async (transactionId, options = {}) => {
  const body = {
    TRANSACTION_ID: transactionId,
  };
  return postEncryptedJson(
    API_ROUTES.CREATE_PAY_BY_CARD_HOSTED_PAYMENT,
    body,
    options,
  );
};

/**
 * Cancel cash-in. BODY: { REQUEST_TOKEN }.
 */
const cancelCashInMoneyOperation = async (requestToken, options = {}) => {
  return postEncryptedJson(
    API_ROUTES.CANCEL_CASHIN_MONEY_OPERATION,
    { REQUEST_TOKEN: requestToken },
    options,
  );
};

/**
 * Cancel cash-out. BODY: { REQUEST_TOKEN }.
 */
const cancelCashOutMoneyOperation = async (requestToken, options = {}) => {
  return postEncryptedJson(
    API_ROUTES.CANCEL_CASH_OUT_OPERATION,
    { REQUEST_TOKEN: requestToken },
    options,
  );
};

/**
 * Apply internal transfer money operation (password + TRANSACTION_ID).
 */
const applyInternalTransferMoneyOperation = async (
  transactionId,
  plainPassword,
  options = {},
) => {
  const hashed = await hashPassword(plainPassword);
  const body = { TRANSACTION_ID: transactionId, PASSWORD: hashed };
  return postEncryptedJson(
    API_ROUTES.APPLY_INTERNAL_TRANSFER_MONEY_OPERATION,
    body,
    options,
  );
};

/**
 * Cancel internal transfer money operation.
 */
const cancelInternalTransferMoneyOperation = async (
  transactionId,
  options = {},
) => {
  return postEncryptedJson(
    API_ROUTES.CANCEL_INTERNAL_TRANSFER_MONEY_OPERATION,
    { TRANSACTION_ID: transactionId },
    options,
  );
};

/**
 * Apply exchange rate operation (password + EXCHANGE_ID).
 * Mirrors mobile DialogWidget logic for APPLY_EXCHANGE_RATE.
 */
const applyExchangeRateOperation = async (
  exchangeId,
  plainPassword,
  options = {},
) => {
  const hashed = await hashPassword(plainPassword);
  const body = { EXCHANGE_ID: exchangeId, PASSWORD: hashed };
  return postEncryptedJson(API_ROUTES.APPLY_EXCHANGE_RATE, body, options);
};

/**
 * Cancel exchange rate operation. BODY: { TRANSACTION_ID }.
 * Mirrors mobile DialogWidget logic for CANCEL_EXCHANGE_RATE.
 */
const cancelExchangeRateOperation = async (exchangeId, options = {}) => {
  return postEncryptedJson(
    API_ROUTES.CANCEL_EXCHANGE_RATE,
    { TRANSACTION_ID: exchangeId },
    options,
  );
};

/**
 * Update PIN / password. BODY: { OLD, NEW, NEW_CONFIRMATION } (plain text; server hashes).
 */
const updatePinCode = async (oldPin, newPin, newConfirmation, options = {}) => {
  const body = {
    OLD: String(oldPin ?? ""),
    NEW: String(newPin ?? ""),
    NEW_CONFIRMATION: String(newConfirmation ?? ""),
  };
  return postEncryptedJson(API_ROUTES.UPDATE_PIN_CODE, body, options);
};

/**
 * Get user notification settings.
 * Mirrors Flutter SecurityScreen._loadingData().
 */
const getUserNotificationSetting = async (options = {}) => {
  return postEncryptedJson(
    API_ROUTES.GET_USER_NOTIFICATION_SETTING,
    {},
    options,
  );
};

/**
 * In-app notification list (paginated). BODY: { PER_PAGE }.
 * Marks unread notifications as seen on the server (matches Laravel NotificationController::getNotifications).
 */
const getClientNotifications = async (perPage = 15, options = {}) => {
  const n = Math.max(1, Math.min(50, Number(perPage) || 15));
  return postEncryptedJson(
    API_ROUTES.GET_NOTIFICATIONS,
    { PER_PAGE: n },
    options,
  );
};

/**
 * Update user notification settings.
 * Mirrors Flutter SecurityScreen._updateSecuritySettings().
 * BODY: { EMAIL: boolean }
 */
const updateUserNotificationSetting = async (emailEnabled, options = {}) => {
  return postEncryptedJson(
    API_ROUTES.UPDATE_USER_NOTIFICATION_SETTING,
    { EMAIL: !!emailEnabled },
    options,
  );
};

/**
 * Delete currently logged-in user account.
 * Mirrors Flutter SecurityScreen._deleteUserAccount().
 * BODY: { PASSWORD: plainPin }
 */
const deleteUserAccount = async (plainPin, options = {}) => {
  return postEncryptedJson(
    API_ROUTES.DELETE_USER_ACCOUNT,
    { PASSWORD: String(plainPin ?? "") },
    options,
  );
};

/**
 * Get the list of devices connected to the logged-in account.
 * Mirrors Flutter ActiveDeviceScreen._loadingData().
 */
const getDevicesList = async (options = {}) => {
  return postEncryptedJson(API_ROUTES.GET_DEVICES_LIST, {}, options);
};

/**
 * Get a single device details by ID.
 * Mirrors Flutter EditDeviceScreen._loadingData().
 * BODY: { DEVICE_ID: id }
 */
const getDevice = async (deviceId, options = {}) => {
  return postEncryptedJson(
    API_ROUTES.GET_DEVICE,
    { DEVICE_ID: deviceId },
    options,
  );
};

/**
 * Update a device settings by ID.
 * Mirrors Flutter EditDeviceScreen._saveDeviceSettings().
 * BODY: { DEVICE_ID, PERMISSION, IS_OWNER, IS_ACTIVE, RECEIVE_NOTIFICATION }
 */
const updateDevice = async (payload = {}, options = {}) => {
  return postEncryptedJson(
    API_ROUTES.UPDATE_DEVICE,
    {
      DEVICE_ID: payload.DEVICE_ID,
      PERMISSION: payload.PERMISSION,
      IS_OWNER: payload.IS_OWNER,
      IS_ACTIVE: payload.IS_ACTIVE,
      RECEIVE_NOTIFICATION: payload.RECEIVE_NOTIFICATION,
    },
    options,
  );
};

/**
 * Delete a device by ID.
 * Mirrors Flutter ActiveDeviceScreen._deleteDevice().
 * BODY: { DEVICE_ID: id }
 */
const deleteDevice = async (deviceId, options = {}) => {
  return postEncryptedJson(
    API_ROUTES.DELETE_DEVICE,
    { DEVICE_ID: deviceId },
    options,
  );
};

/**
 * Delete push token on logout (mobile-aligned).
 * Mirrors Flutter firebaseHelper.deleteToken(username) behavior:
 * call backend with BODY: { USERNAME } and ignore failures during local logout.
 */
const deleteFcmTokenOnLogout = async (username, options = {}) => {
  if (!username || String(username).trim() === "") return null;
  try {
    return await postEncryptedJson(
      API_ROUTES.FCM_DELETE,
      { USERNAME: String(username).trim() },
      options,
    );
  } catch (_) {
    return null;
  }
};

/**
 * Register/update push token after successful login (mobile-aligned).
 * BODY: { FCM_TOKEN, USERNAME }
 */
const registerFcmTokenOnLogin = async (username, fcmToken, options = {}) => {
  const normalizedUsername = String(username || "").trim();
  const normalizedToken = String(fcmToken || "").trim();
  if (!normalizedUsername || !normalizedToken) return null;

  try {
    return await postEncryptedJson(
      API_ROUTES.FCM_UPDATE,
      {
        FCM_TOKEN: normalizedToken,
        USERNAME: normalizedUsername,
      },
      options,
    );
  } catch (_) {
    return null;
  }
};

/**
 * Get trading accounts list. BODY: { ACCOUNT_TYPE, PER_PAGE, PAGE }.
 * Returns { ...response, decryptedData }.
 */
const getTradingAccounts = async (
  accountType = 0,
  perPage = 6,
  page = 1,
  searchTerm = null,
  options = {},
) => {
  if (!getTradingAccounts._cache) getTradingAccounts._cache = new Map();
  if (!getTradingAccounts._inFlight) getTradingAccounts._inFlight = new Map();
  const forceRefresh = options?.forceRefresh === true;

  const cacheKey = JSON.stringify({
    accountType,
    perPage,
    page,
    search: searchTerm ? searchTerm.trim() : null,
    baseURL: options?.baseURL || "",
  });
  const cacheTtlMs = 60 * 1000;
  const now = Date.now();

  if (!forceRefresh) {
    const cached = getTradingAccounts._cache.get(cacheKey);
    if (cached && now - cached.timestamp < cacheTtlMs) {
      return cached.value;
    }
  }

  if (!forceRefresh) {
    const inFlight = getTradingAccounts._inFlight.get(cacheKey);
    if (inFlight) {
      return await inFlight;
    }
  }

  const body = {
    ACCOUNT_TYPE: accountType,
    PER_PAGE: perPage,
    PAGE: page,
  };
  if (searchTerm && searchTerm.trim() !== "") {
    body.SEARCH = searchTerm.trim();
  }
  const reqPromise = postEncryptedJson(
    API_ROUTES.GET_TRADING_ACCOUNTS,
    body,
    options,
  );
  getTradingAccounts._inFlight.set(cacheKey, reqPromise);

  try {
    const response = await reqPromise;
    const decrypted = response?.decryptedData || response?.data;
    if (decrypted && Number(decrypted.CODE) === AppConstants.responseOk) {
      getTradingAccounts._cache.set(cacheKey, {
        timestamp: Date.now(),
        value: response,
      });
    }
    return response;
  } finally {
    getTradingAccounts._inFlight.delete(cacheKey);
  }
};

/** Clears in-memory list cache so the next getTradingAccounts call hits the server (e.g. after creating an account). */
const invalidateTradingAccountsCache = () => {
  if (getTradingAccounts._cache) {
    getTradingAccounts._cache.clear();
  }
};

/**
 * Get all agent (IB) clients.
 * Backend endpoint: /v1/user/get-all-agent-clients?page={page}
 * BODY (encrypted): { WITH_DOC, SEARCH } (+ optional SHOW_TREE/USER_ID)
 */
const getAllAgentClients = async (
  page = 1,
  withDoc = 1,
  search = null,
  showTree = undefined,
  userId = undefined,
  options = {},
) => {
  if (!getAllAgentClients._cache) getAllAgentClients._cache = new Map();
  if (!getAllAgentClients._inFlight) getAllAgentClients._inFlight = new Map();

  const cacheKey = JSON.stringify({
    page,
    withDoc,
    search: search || null,
    showTree: showTree ?? null,
    userId: userId ?? null,
    baseURL: options?.baseURL || "",
  });
  const cacheTtlMs = 30 * 1000;
  const cached = getAllAgentClients._cache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < cacheTtlMs) {
    return cached.value;
  }
  const inFlight = getAllAgentClients._inFlight.get(cacheKey);
  if (inFlight) return await inFlight;

  const body = {
    WITH_DOC: withDoc,
    SEARCH: search,
  };
  if (showTree !== undefined) body.SHOW_TREE = showTree;
  if (userId !== undefined) body.USER_ID = userId;
  const endpoint = `${API_ROUTES.GET_ALL_AGENT_CLIENTS}?page=${page}`;
  const requestPromise = postEncryptedJson(endpoint, body, options);
  getAllAgentClients._inFlight.set(cacheKey, requestPromise);
  try {
    const response = await requestPromise;
    const code = Number(response?.decryptedData?.CODE ?? response?.data?.CODE);
    if (code === AppConstants.responseOk) {
      getAllAgentClients._cache.set(cacheKey, {
        timestamp: Date.now(),
        value: response,
      });
    }
    return response;
  } finally {
    getAllAgentClients._inFlight.delete(cacheKey);
  }
};

const getAgentClientAccounts = async (userId, options = {}) => {
  if (!getAgentClientAccounts._cache) getAgentClientAccounts._cache = new Map();
  if (!getAgentClientAccounts._inFlight)
    getAgentClientAccounts._inFlight = new Map();
  const forceRefresh = options?.forceRefresh === true;

  const cacheKey = JSON.stringify({
    userId: userId ?? null,
    baseURL: options?.baseURL || "",
  });
  const cacheTtlMs = 20 * 1000;
  if (!forceRefresh) {
    const cached = getAgentClientAccounts._cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < cacheTtlMs) {
      return cached.value;
    }
  }
  if (!forceRefresh) {
    const inFlight = getAgentClientAccounts._inFlight.get(cacheKey);
    if (inFlight) return await inFlight;
  }

  const body = {
    USER_ID: userId,
  };
  const requestPromise = postEncryptedJson(
    API_ROUTES.GET_AGENT_CLIENT_ACCOUNTS,
    body,
    options,
  );
  getAgentClientAccounts._inFlight.set(cacheKey, requestPromise);
  try {
    const response = await requestPromise;
    const code = Number(response?.decryptedData?.CODE ?? response?.data?.CODE);
    if (code === AppConstants.responseOk) {
      getAgentClientAccounts._cache.set(cacheKey, {
        timestamp: Date.now(),
        value: response,
      });
    }
    return response;
  } finally {
    getAgentClientAccounts._inFlight.delete(cacheKey);
  }
};

/**
 * Fetch a document image from the backend.
 * Mirrors Flutter's NetworkImage(url, headers: AppConstants.getHeader()).
 * Flutter getHeader() sends: Authorization + device-id/os/model/ip/position
 * (removes Content-Type, secret-key, user-type from extraInfo).
 */
const fetchDocumentImage = async (filePath) => {
  if (!filePath) return null;
  const IMAGE_TYPE_DOCUMENT = 1;
  const normalizedPath = String(filePath).replace(/^\/+/, "");
  const encodedPath = normalizedPath
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/");

  const configuredBase = (process.env.REACT_APP_IMAGE_BASE_URL || "").trim();
  const baseUrl =
    configuredBase ||
    (process.env.NODE_ENV === "development"
      ? "/api-images"
      : `${APIURI}v1/user/images`);
  const url = `${baseUrl.replace(/\/+$/, "")}/${encodedPath}?type=${IMAGE_TYPE_DOCUMENT}`;

  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");
  const stored = getStoredDeviceHeaders() || {};

  const headers = {};
  if (token && token !== "FIRST_TIME_LOGIN") {
    headers["Authorization"] = `Bearer ${token}`;
  }
  if (stored["device-id"]) headers["device-id"] = stored["device-id"];
  if (stored["device-os"]) headers["device-os"] = stored["device-os"];
  if (stored["device-model"]) headers["device-model"] = stored["device-model"];
  headers["device-ip"] = "null";
  headers["device-position"] = JSON.stringify({ LATITUDE: 0, LONGTITUDE: 0 });

  try {
    const resp = await axios.get(url, { headers, responseType: "blob" });
    if (resp.data && resp.data.size > 0) {
      if (resp.data.type && resp.data.type.startsWith("image/")) {
        return URL.createObjectURL(resp.data);
      }
      const text = await resp.data.text();
      try {
        const json = JSON.parse(text);
        if (json.DATA) {
          return `data:image/png;base64,${json.DATA}`;
        }
      } catch {}
    }
    return null;
  } catch (err) {
    console.warn("[fetchDocumentImage] error for", filePath, ":", err.message);
    return null;
  }
};

const fetchUserFileDocument = async (directory, documentName, options = {}) => {
  const safeDirectory = String(directory || "").trim();
  const safeDocumentName = String(documentName || "").trim();
  if (!safeDirectory || !safeDocumentName) {
    throw new Error("Directory and document name are required.");
  }

  const { baseURL = "" } = options;
  const base = baseURL
    ? `${baseURL.replace(/\/$/, "")}/v1/user/get-file-document`
    : API_ROUTES.GET_FILE_DOCUMENT;
  const fullUrl = `${base}/${encodeURIComponent(safeDirectory)}/${encodeURIComponent(
    safeDocumentName,
  )}`;

  const extraInfo = await buildExtraInfo();
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");
  const headers = {
    "device-id": extraInfo["device-id"] ?? "",
    "device-model": extraInfo["device-model"] ?? "",
    "device-os": extraInfo["device-os"] ?? "",
    "device-ip": extraInfo["device-ip"] ?? "null",
    "device-position":
      extraInfo["device-position"] ??
      JSON.stringify({ LATITUDE: 0, LONGTITUDE: 0 }),
  };
  if (token && token !== "FIRST_TIME_LOGIN") {
    headers.Authorization = `Bearer ${token}`;
  }

  return axios.get(fullUrl, { headers, responseType: "blob" });
};

export {
  API_ROUTES,
  fetchAndStoreServerPublicKey,
  fetchDocumentImage,
  fetchUserFileDocument,
  checkAgentTradingAccount,
  verifyEmailDemoAccount,
  verifyOTPDemoAccount,
  setNewPassword,
  createUserLoginMT5Account,
  createUrbanInfoMT5Account,
  createRegulationCompanyMT5Account,
  createUserDocumentsMT5Account,
  getAgreements,
  getFaq,
  getTutorialInfo,
  getAppUrl,
  getHtmlPage,
  getProfile,
  updateProfile,
  updateProfileStatus,
  updatePersonalInformation,
  getHomePageInfos,
  getIbDashboard,
  getMarketingPlans,
  getMarketingPlanAttachment,
  getBrokerTools,
  getBrokerToolTemplate,
  saveBrokerEditingTemplate,
  getAllEWalletsInfo,
  getAllEWalletsAmount,
  getChatMessages,
  addChatMessage,
  addChatDocument,
  getChatDocument,
  signForcedAgreement,
  createIdentityVerificationWithDocuments,
  updateEmail,
  validateOpt,
  getTradingInfos,
  updateTradingInfos,
  getFinancialInfos,
  updateFinancialInfos,
  deleteFinancialInfos,
  getCashInMethod,
  getTradingAccountsTrx,
  buildExtraInfo,
  ensureLocation,
  getStoredDeviceHeaders,
  clearStoredDeviceHeaders,
  checkCtsMustSign,
  viewCtsToday,
  createCtsToday,
  signCts,
  createCashInOperation,
  createPayByCardOperation,
  applyPayByCardOperation,
  cancelPayByCardOperation,
  addResultPayByCardOperation,
  createPayByCardHostedPayment,
  createInternalTransferMoneyOperation,
  getCashOutPaymentMethod,
  createCashOutOperation,
  applyCashInMoneyOperation,
  applyCashOutMoneyOperation,
  getCollectedMoneyRequest,
  getTransferCashOutRequest,
  applyCollectCashInRequest,
  applyCollectCashOutRequest,
  applyTransferCashOutRequest,
  cancelCashInMoneyOperation,
  cancelCashOutMoneyOperation,
  applyInternalTransferMoneyOperation,
  cancelInternalTransferMoneyOperation,
  applyExchangeRateOperation,
  cancelExchangeRateOperation,
  getTradingAccounts,
  invalidateTradingAccountsCache,
  createNewTradingAccount,
  updateTradingAccountInfos,
  setTradingAccountName,
  resetMt5Password,
  getAllAgentClients,
  getAgentClientAccounts,
  createDepositWithdrawOperation,
  applyDepositWithdrawOperation,
  cancelDepositWithdrawOperation,
  cancelDepositDemoOperation,
  sendNotifications,
  updatePinCode,
  getUserNotificationSetting,
  getClientNotifications,
  updateUserNotificationSetting,
  deleteUserAccount,
  getDevicesList,
  getDevice,
  updateDevice,
  deleteDevice,
  registerFcmTokenOnLogin,
  deleteFcmTokenOnLogout,
  encryptData,
  decryptResponseMessages,
  getApiResponsePayload,
  getResponseCode,
  isApiResponseOk,
  postEncryptedJson,
  postEncryptedFormData,
  getTradingAccountInfos,
  getClientsTradingHistory,
  getClientsAccountTotals,
  getAccountTradingHistory,
  getUserTradingVolume,
  verifyUserPassword,
  sendStatementOfAccountEmail,
  getAppUrls,
};

// Copy Trade API functions
const getCopyTradeMasters = async (body = {}, options = {}) => {
  return postEncryptedJson(API_ROUTES.GET_COPY_TRADE_MASTERS, body, options);
};

/**
 * Become a copy-trade master (same create fields as admin upsert).
 * BODY: { NAME, LOGIN, COMMISSION_PERCENT, COMMISSION_LOGIN }
 */
const becomeCopyTradeMaster = async (body = {}, options = {}) => {
  return postEncryptedJson(API_ROUTES.BECOME_COPY_TRADE_MASTER, body, options);
};

/**
 * Update approved master profile.
 * BODY: { LOGIN, COMMISSION_PERCENT, COMMISSION_LOGIN }
 */
const updateCopyTradeMaster = async (body = {}, options = {}) => {
  return postEncryptedJson(API_ROUTES.UPDATE_COPY_TRADE_MASTER, body, options);
};

/**
 * List slave/subscriber accounts under the current approved master.
 * Optional body: { SEARCH, PER_PAGE, page }
 */
const getCopyTradeMasterSubscribers = async (body = {}, options = {}) => {
  return postEncryptedJson(API_ROUTES.GET_COPY_TRADE_MASTER_SUBSCRIBERS, body, options);
};

/**
 * Closed MT5 trades for an enabled master, for one week.
 * BODY: { LOGIN, FROM?, TO? } — FROM/TO are unix seconds; defaults to current UTC week.
 */
const getCopyTradeMasterTrades = async (body = {}, options = {}) => {
  return postEncryptedJson(API_ROUTES.GET_COPY_TRADE_MASTER_TRADES, body, options);
};

/**
 * Max drawdown, slave profit %, working days, equity, and closed-trade P&L points for a master.
 * BODY: { LOGIN }
 */
const getCopyTradeMasterPerformance = async (body = {}, options = {}) => {
  return postEncryptedJson(API_ROUTES.GET_COPY_TRADE_MASTER_PERFORMANCE, body, options);
};

/**
 * Subscribe/copy a master: creates slave + enabled rule.
 * BODY: { MASTER_LOGIN, LOGIN, NAME, RATIO, LOSS_LIMIT_PERCENT, PASSWORD }
 * PASSWORD is hashed before being sent, mirroring cash-in / cash-out.
 */
const subscribeCopyTradeMaster = async (body = {}, options = {}) => {
  const payload = body && typeof body === "object" ? { ...body } : {};
  if (payload.PASSWORD) {
    payload.PASSWORD = await hashPassword(payload.PASSWORD);
  }
  return postEncryptedJson(API_ROUTES.COPY_TRADE_SUBSCRIBE, payload, options);
};

/**
 * Masters the current user is actively copying.
 */
const getCopyTradeMySubscriptions = async (options = {}) => {
  return postEncryptedJson(API_ROUTES.COPY_TRADE_MY_SUBSCRIPTIONS, {}, options);
};

/**
 * Disconnect from a master. BODY: { MASTER_LOGIN, PASSWORD }
 * PASSWORD is hashed before being sent, mirroring cash-in / cash-out.
 */
const unsubscribeCopyTradeMaster = async (body = {}, options = {}) => {
  const payload = body && typeof body === "object" ? { ...body } : {};
  if (payload.PASSWORD) {
    payload.PASSWORD = await hashPassword(payload.PASSWORD);
  }
  return postEncryptedJson(API_ROUTES.COPY_TRADE_UNSUBSCRIBE, payload, options);
};

/**
 * Current user's master application status: none | pending | approved.
 */
const getCopyTradeMasterStatus = async (options = {}) => {
  return postEncryptedJson(API_ROUTES.GET_COPY_TRADE_MASTER_STATUS, {}, options);
};

/**
 * Live MT5 symbol quotes for the Copy Trade ticker.
 * Optional body: { SYMBOLS: ["BTCUSD", ...] }
 */
const getCopyTradeSymbolQuotes = async (body = {}, options = {}) => {
  return postEncryptedJson(API_ROUTES.GET_COPY_TRADE_SYMBOL_QUOTES, body, options);
};

const getTradingAccountInfos = async (accountId, agentView = false, options = {}) => {
  const { baseURL = "" } = options;
  if (accountId == null || accountId === "") {
    throw new Error("ACCOUNT_ID is required to load trading account information.");
  }
  const body = { ACCOUNT_ID: String(accountId) };
  if (agentView) body.AGENT_VIEW = "true";
  return postEncryptedJson(API_ROUTES.GET_TRADING_ACCOUNT_INFOS, body, { baseURL });
};

const getClientsTradingHistory = async (dateFrom, dateTo, options = {}) => {
  return postEncryptedJson(
    API_ROUTES.GET_CLIENTS_TRADING_HISTORY,
    { DATE_FROM: dateFrom, DATE_TO: dateTo },
    options,
  );
};

const getClientsAccountTotals = async (options = {}) => {
  return postEncryptedJson(API_ROUTES.GET_CLIENTS_ACCOUNT_TOTALS, {}, options);
};

const getUserTradingVolume = async (
  { accountType = 0, userId = null, dateFrom = null, dateTo = null } = {},
  options = {},
) => {
  const body = { ACCOUNT_TYPE: accountType };
  if (userId != null && userId !== "") body.USER_ID = userId;
  if (dateFrom) body.DATE_FROM = dateFrom;
  if (dateTo) body.DATE_TO = dateTo;
  return postEncryptedJson(API_ROUTES.GET_USER_TRADING_VOLUME, body, options);
};

const getAccountTradingHistory = async (
  { tradingId, page = 1, perPage = 10, dateFrom = null, dateTo = null, search = null } = {},
  options = {},
) => {
  const body = {
    TRADING_ID: tradingId,
    PER_PAGE: perPage,
    DATE_FROM: dateFrom,
    DATE_TO: dateTo,
    SEARCH: search,
  };
  const url = `${API_ROUTES.GET_ACCOUNT_TRADING_HISTORY}?page=${page}`;
  return postEncryptedJson(url, body, options);
};

const verifyUserPassword = async (plainPassword, options = {}) => {
  const hashed = await hashPassword(plainPassword);
  return postEncryptedJson(API_ROUTES.VERIFY_PIN_CODE, { PASSWORD: hashed }, options);
};

const sendStatementOfAccountEmail = async (
  { file, filename, fullName, dateFrom, dateTo, pdfBase64 } = {},
  options = {},
) => {
  const toDateValue = (value) => {
    if (!value) return null;
    if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString();
    return String(value);
  };

  let pdfFile = file instanceof Blob ? file : null;
  if (!pdfFile && pdfBase64) {
    const raw = String(pdfBase64).replace(/^data:application\/pdf;base64,/i, "");
    const binary = Uint8Array.from(atob(raw), (c) => c.charCodeAt(0));
    pdfFile = new File([binary], filename || "statement-of-account.pdf", {
      type: "application/pdf",
    });
  }
  if (!pdfFile) throw new Error("Statement PDF is required.");
  if (!(pdfFile instanceof File)) {
    pdfFile = new File([pdfFile], filename || pdfFile.name || "statement-of-account.pdf", {
      type: pdfFile.type || "application/pdf",
    });
  }

  return postEncryptedFormData(
    API_ROUTES.SEND_STATEMENT_OF_ACCOUNT,
    {
      FULL_NAME: String(fullName || ""),
      FILE_NAME: String(filename || pdfFile.name || "statement-of-account.pdf"),
      DATE_FROM: toDateValue(dateFrom),
      DATE_TO: toDateValue(dateTo),
    },
    { STATEMENT_PDF: pdfFile },
    options,
  );
};

const getAppUrls = async (options = {}) => {
  const result = { android: "", ios: "" };
  try {
    const wrapped = await postEncryptedJson(API_ROUTES.GET_APP_URL, {}, options);
    const data = wrapped?.decryptedData || wrapped?.data || {};
    if (Number(data?.CODE) === AppConstants.responseOk) {
      result.android = data?.ANDROID_URL || "";
      result.ios = data?.IOS_URL || "";
    }
  } catch (_) {}
  return result;
};

export default postDataToServer;
export {
  getCopyTradeMasters,
  getCopyTradeMySubscriptions,
  subscribeCopyTradeMaster,
  unsubscribeCopyTradeMaster,
  becomeCopyTradeMaster,
  updateCopyTradeMaster,
  getCopyTradeMasterStatus,
  getCopyTradeSymbolQuotes,
  getCopyTradeMasterTrades,
  getCopyTradeMasterPerformance,
  getCopyTradeMasterSubscribers,
};
